import React, { useState } from 'react';
import { Card } from '../../components/ui/Card';
import { 
  Filter,
  Download,
  RefreshCw,
  ShoppingBag,
  ChevronDown,
  ChevronRight,
  LayoutList,
  ArrowUpRight,
  ArrowDown,
  CheckCircle,
  ArrowRight,
  Bell,
  Calendar
} from 'lucide-react';

export default function ProductPortfolioAnalysis2() {
  const [dateRange, setDateRange] = useState('today');
  const [isExporting, setIsExporting] = useState(false);
  const [rfMode, setRfMode] = useState(true); // true: RF only, false: RFM

  const handleExportPDF = async () => {
    setIsExporting(true);
    setTimeout(() => setIsExporting(false), 2000);
  };

  // Problematik elastikiyet hesaplama kodunu kaldırdık
  // Bu kod ProductPortfolioAnalysis sayfasında değil, PriceElasticityOfDemand sayfasında olmalı

  return (
    <div className="p-6 space-y-8">
      {/* Header Section */}
      <div className="flex flex-col gap-6 mb-8">
        {/* Üst Kısım - Başlık ve Butonlar */}
        <div className="flex items-center justify-between bg-white p-6 rounded-2xl shadow-sm">
          <div className="flex items-center gap-4">
            <img 
              src="/Google-Analytics-Logo.png" 
              alt="Analytics Icon" 
              className="w-12 h-12 object-contain"
            />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Product Portfolio Analysis</h1>
              <p className="text-sm text-gray-500 mt-1">Strategic analysis of your product portfolio performance</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button className="relative p-2 hover:bg-gray-100 rounded-xl transition-colors">
              <Bell className="w-5 h-5 text-gray-600" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>
            <button className="bg-white shadow-[0_4px_15px_rgba(0,0,0,0.08)] hover:shadow-[0_8px_20px_rgba(0,0,0,0.12)] border border-gray-200 rounded-xl px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-all duration-300 flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Filters
            </button>
            <button
              onClick={handleExportPDF}
              disabled={isExporting}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors"
            >
              {isExporting ? (
                <>
                  <span className="animate-spin">⏳</span>
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Export Report
                </>
              )}
            </button>
          </div>
        </div>

        {/* Alt Kısım - Tarih Filtreleri */}
        <div className="flex items-center justify-between">
          {/* Sol taraf - Hızlı Tarih Seçimleri */}
          <div className="inline-flex bg-white rounded-xl shadow-[0_4px_15px_rgba(0,0,0,0.08)] p-1 border border-gray-200">
            <button
              onClick={() => setDateRange('today')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                dateRange === 'today' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Today
            </button>
            <button
              onClick={() => setDateRange('yesterday')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                dateRange === 'yesterday' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Yesterday
            </button>
            <button
              onClick={() => setDateRange('7days')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                dateRange === '7days' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              7 Days
            </button>
            <button
              onClick={() => setDateRange('30days')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                dateRange === '30days' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              30 Days
            </button>
            <button
              onClick={() => setDateRange('thisMonth')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                dateRange === 'thisMonth' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              This Month
            </button>
          </div>

          {/* Sağ taraf - Custom Range Butonu */}
          <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
            <Calendar className="w-4 h-4" />
            Custom Range
          </button>
        </div>
      </div>

      <div className="w-full">
        {/* Boston Matrix tablosu */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200 mb-6">
          {/* Header kısmı */}
          <div className="bg-gradient-to-r from-purple-800 to-violet-900 text-white p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h3 className="text-base sm:text-lg font-semibold">Product Positioning Strategy Performance Over 8 Weeks</h3>
                <p className="text-xs sm:text-sm text-purple-100 mt-1">The impact of promoting Stars category products by 60%</p>
              </div>
              <span className="inline-flex items-center px-3 py-1 bg-green-500 text-white text-xs sm:text-sm rounded-full gap-1 whitespace-nowrap">
                <ArrowUpRight className="w-3.5 h-3.5" />
                <span>Total Revenue: +37.8%</span>
              </span>
            </div>
          </div>
          
          {/* Table container - Yatay scroll için */}
          <div className="relative overflow-x-auto" style={{ scrollBehavior: 'smooth' }}>
            {/* Scroll shadow indicators */}
            <div className="absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-white to-transparent pointer-events-none z-20"></div>
            <div className="absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-l from-white to-transparent pointer-events-none z-20"></div>
            
            {/* Table */}
            <table className="w-full" style={{ minWidth: '1000px' }}>
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-3 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 w-[200px]">CATEGORY</th>
                  {/* Her hafta için daha dar sabit genişlik belirledim */}
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[170px]">WEEK 1</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[170px]">WEEK 2</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[170px]">WEEK 3</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[170px]">WEEK 4</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[170px]">WEEK 5</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[170px]">WEEK 6</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[170px]">WEEK 7</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[170px]">WEEK 8</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* Stars */}
                <tr className="hover:bg-green-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-green-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <span className="text-green-600 text-xl">★</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Stars</span>
                        <span className="text-gray-500 text-sm">~25 Products</span>
                      </div>
                    </div>
                  </td>
                  {/* Week 1 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺115,200</div>
                      <div className="text-sm text-gray-500">Conversion: 5.4%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 48,500 <span className="text-green-600 font-medium">(40.6%)</span></div>
                        <div className="text-sm text-gray-500">RPV: ₺2.37</div>
                        <div className="text-sm text-gray-500">Products: 15</div>
                      </div>
                    </div>
                  </td>
                  {/* Stars - Week 2 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺142,800</div>
                      <div className="text-sm text-gray-500">Conversion: 5.2%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 62,300 <span className="text-green-600 font-medium">(52.6%)</span></div>
                        <div className="text-sm text-gray-500">RPV: ₺2.29</div>
                        <div className="text-sm text-gray-500">Products: 18 (+3)</div>
                      </div>
                    </div>
                  </td>
                  {/* Stars - Week 3 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺168,500</div>
                      <div className="text-sm text-gray-500">Conversion: 5.0%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 76,800 <span className="text-green-600 font-medium">(58.1%)</span></div>
                        <div className="text-sm text-gray-500">RPV: ₺2.19</div>
                        <div className="text-sm text-gray-500">Products: 22 (+4)</div>
                      </div>
                    </div>
                  </td>
                  {/* Stars - Week 4 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺196,500</div>
                      <div className="text-sm text-gray-500">Conversion: 4.9%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 92,400 <span className="text-green-600 font-medium">(62.1%)</span></div>
                        <div className="text-sm text-gray-500">RPV: ₺2.13</div>
                        <div className="text-sm text-gray-500">Products: 25 (+3)</div>
                      </div>
                    </div>
                  </td>
                  {/* Stars - Week 5 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺205,200</div>
                      <div className="text-sm text-gray-500">Conversion: 4.8%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 97,500 <span className="text-green-600 font-medium">(64.3%)</span></div>
                        <div className="text-sm text-gray-500">RPV: ₺2.10</div>
                        <div className="text-sm text-gray-500">Products: 25 (+0)</div>
                      </div>
                    </div>
                  </td>
                  {/* Stars - Week 6 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺214,800</div>
                      <div className="text-sm text-gray-500">Conversion: 4.8%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 102,300 <span className="text-green-600 font-medium">(65.8%)</span></div>
                        <div className="text-sm text-gray-500">RPV: ₺2.10</div>
                        <div className="text-sm text-gray-500">Products: 26 (+1)</div>
                      </div>
                    </div>
                  </td>
                  {/* Stars - Week 7 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺224,500</div>
                      <div className="text-sm text-gray-500">Conversion: 4.7%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 108,800 <span className="text-green-600 font-medium">(67.2%)</span></div>
                        <div className="text-sm text-gray-500">RPV: ₺2.06</div>
                        <div className="text-sm text-gray-500">Products: 26 (+0)</div>
                      </div>
                    </div>
                  </td>
                  {/* Stars - Week 8 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺230,500</div>
                      <div className="text-sm text-gray-500">Conversion: 4.7%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 112,400 <span className="text-green-600 font-medium">(68.5%)</span></div>
                        <div className="text-sm text-gray-500">RPV: ₺2.05</div>
                        <div className="text-sm text-gray-500">Products: 27 (+1)</div>
                      </div>
                    </div>
                  </td>
                </tr>
                
                {/* Separator Row */}
                <tr className="h-2 bg-gray-100">
                  <td colSpan="9" className="p-0"></td>
                </tr>
                
                {/* Question Marks */}
                <tr className="hover:bg-yellow-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-yellow-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                        <span className="text-yellow-600 text-xl">?</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Question Marks</span>
                        <span className="text-gray-500 text-sm">~20 Products</span>
                      </div>
                    </div>
                  </td>
                  {/* Week 1 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺78,500</div>
                      <div className="text-sm text-gray-500">Conversion: 2.8%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 36,400 <span className="text-yellow-600 font-medium">(30.4%)</span></div>
                        <div className="text-sm text-gray-500">RPV: ₺2.16</div>
                        <div className="text-sm text-gray-500">Products: 24</div>
                      </div>
                    </div>
                  </td>
                  {/* Question Marks - Week 2 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺72,300</div>
                      <div className="text-sm text-gray-500">Conversion: 2.7%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 32,100 <span className="text-yellow-600 font-medium">(27.1%)</span></div>
                        <div className="text-sm text-gray-500">RPV: ₺2.25</div>
                        <div className="text-sm text-gray-500">Products: 22 (-2)</div>
                      </div>
                    </div>
                  </td>
                  {/* Question Marks - Week 3 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺68,400</div>
                      <div className="text-sm text-gray-500">Conversion: 2.6%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 28,500 <span className="text-yellow-600 font-medium">(21.6%)</span></div>
                        <div className="text-sm text-gray-500">RPV: ₺2.40</div>
                        <div className="text-sm text-gray-500">Products: 20 (-2)</div>
                      </div>
                    </div>
                  </td>
                  {/* Question Marks - Week 4 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺65,200</div>
                      <div className="text-sm text-gray-500">Conversion: 2.5%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 25,800 <span className="text-yellow-600 font-medium">(17.3%)</span></div>
                        <div className="text-sm text-gray-500">RPV: ₺2.53</div>
                        <div className="text-sm text-gray-500">Products: 18 (-2)</div>
                      </div>
                    </div>
                  </td>
                  {/* Question Marks - Week 5 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺62,800</div>
                      <div className="text-sm text-gray-500">Conversion: 2.4%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 23,400 <span className="text-yellow-600 font-medium">(15.4%)</span></div>
                        <div className="text-sm text-gray-500">RPV: ₺2.68</div>
                        <div className="text-sm text-gray-500">Products: 16 (-2)</div>
                      </div>
                    </div>
                  </td>
                  {/* Question Marks - Week 6 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺58,500</div>
                      <div className="text-sm text-gray-500">Conversion: 2.4%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 21,200 <span className="text-yellow-600 font-medium">(13.6%)</span></div>
                        <div className="text-sm text-gray-500">RPV: ₺2.76</div>
                        <div className="text-sm text-gray-500">Products: 15 (-1)</div>
                      </div>
                    </div>
                  </td>
                  {/* Question Marks - Week 7 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺55,200</div>
                      <div className="text-sm text-gray-500">Conversion: 2.3%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 19,500 <span className="text-yellow-600 font-medium">(12.0%)</span></div>
                        <div className="text-sm text-gray-500">RPV: ₺2.83</div>
                        <div className="text-sm text-gray-500">Products: 14 (-1)</div>
                      </div>
                    </div>
                  </td>
                  {/* Question Marks - Week 8 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺52,800</div>
                      <div className="text-sm text-gray-500">Conversion: 2.3%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 18,200 <span className="text-yellow-600 font-medium">(11.1%)</span></div>
                        <div className="text-sm text-gray-500">RPV: ₺2.90</div>
                        <div className="text-sm text-gray-500">Products: 14 (0)</div>
                      </div>
                    </div>
                  </td>
                </tr>
                
                {/* Separator Row */}
                <tr className="h-2 bg-gray-100">
                  <td colSpan="9" className="p-0"></td>
                </tr>
                
                {/* Cash Cows */}
                <tr className="hover:bg-blue-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-blue-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-blue-600 text-xl">$</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Cash Cows</span>
                        <span className="text-gray-500 text-sm">~30 Products</span>
                      </div>
                    </div>
                  </td>
                  {/* Week 1 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺45,800</div>
                      <div className="text-sm text-gray-500">Conversion: 3.6%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 22,500 <span className="text-blue-600 font-medium">(18.8%)</span></div>
                        <div className="text-sm text-gray-500">RPV: ₺2.04</div>
                        <div className="text-sm text-gray-500">Products: 34</div>
                      </div>
                    </div>
                  </td>
                  {/* Cash Cows - Week 2 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺48,200</div>
                      <div className="text-sm text-gray-500">Conversion: 3.5%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 15,800 <span className="text-blue-600 font-medium">(13.3%)</span></div>
                        <div className="text-sm text-gray-500">RPV: ₺3.05</div>
                        <div className="text-sm text-gray-500">Products: 32 (-2)</div>
                      </div>
                    </div>
                  </td>
                  {/* Cash Cows - Week 3 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺50,400</div>
                      <div className="text-sm text-gray-500">Conversion: 3.4%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 17,200 <span className="text-blue-600 font-medium">(13.0%)</span></div>
                        <div className="text-sm text-gray-500">RPV: ₺2.93</div>
                        <div className="text-sm text-gray-500">Products: 30 (-2)</div>
                      </div>
                    </div>
                  </td>
                  {/* Cash Cows - Week 4 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺52,800</div>
                      <div className="text-sm text-gray-500">Conversion: 3.3%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 18,500 <span className="text-blue-600 font-medium">(12.4%)</span></div>
                        <div className="text-sm text-gray-500">RPV: ₺2.85</div>
                        <div className="text-sm text-gray-500">Products: 28 (-2)</div>
                      </div>
                    </div>
                  </td>
                  {/* Cash Cows - Week 5 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺55,500</div>
                      <div className="text-sm text-gray-500">Conversion: 3.2%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 20,200 <span className="text-blue-600 font-medium">(13.0%)</span></div>
                        <div className="text-sm text-gray-500">RPV: ₺2.75</div>
                        <div className="text-sm text-gray-500">Products: 27 (-1)</div>
                      </div>
                    </div>
                  </td>
                  {/* Cash Cows - Week 6 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺58,200</div>
                      <div className="text-sm text-gray-500">Conversion: 3.1%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 22,500 <span className="text-blue-600 font-medium">(13.8%)</span></div>
                        <div className="text-sm text-gray-500">RPV: ₺2.59</div>
                        <div className="text-sm text-gray-500">Products: 26 (-1)</div>
                      </div>
                    </div>
                  </td>
                  {/* Cash Cows - Week 7 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺60,500</div>
                      <div className="text-sm text-gray-500">Conversion: 3.0%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 24,800 <span className="text-blue-600 font-medium">(14.5%)</span></div>
                        <div className="text-sm text-gray-500">RPV: ₺2.44</div>
                        <div className="text-sm text-gray-500">Products: 25 (-1)</div>
                      </div>
                    </div>
                  </td>
                  {/* Cash Cows - Week 8 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺62,800</div>
                      <div className="text-sm text-gray-500">Conversion: 3.3%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 18,500 <span className="text-blue-600 font-medium">(12.4%)</span></div>
                        <div className="text-sm text-gray-500">RPV: ₺2.85</div>
                        <div className="text-sm text-gray-500">Products: 28 (-2)</div>
                      </div>
                    </div>
                  </td>
                </tr>
                
                {/* Separator Row */}
                <tr className="h-2 bg-gray-100">
                  <td colSpan="9" className="p-0"></td>
                </tr>
                
                {/* Dogs */}
                <tr className="hover:bg-red-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-red-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                        <span className="text-red-600 text-xl">✕</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Dogs</span>
                        <span className="text-gray-500 text-sm">~25 Products</span>
                      </div>
                    </div>
                  </td>
                  {/* Week 1 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺15,800</div>
                      <div className="text-sm text-gray-500">Conversion: 1.5%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 12,200</div>
                        <div className="text-sm text-red-600 font-medium">(10.2%)</div>
                        <div className="text-sm text-gray-500">RPV: ₺1.30</div>
                        <div className="text-sm text-gray-500">Products: 27</div>
                      </div>
                    </div>
                  </td>
                  {/* Week 2 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺13,200</div>
                      <div className="text-sm text-gray-500">Conversion: 1.4%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 8,300</div>
                        <div className="text-sm text-red-600 font-medium">(7.0%)</div>
                        <div className="text-sm text-gray-500">RPV: ₺1.59</div>
                        <div className="text-sm text-gray-500">Products: 25 (-2)</div>
                      </div>
                    </div>
                  </td>
                  {/* Week 3 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺11,500</div>
                      <div className="text-sm text-gray-500">Conversion: 1.3%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 6,800</div>
                        <div className="text-sm text-red-600 font-medium">(5.1%)</div>
                        <div className="text-sm text-gray-500">RPV: ₺1.69</div>
                        <div className="text-sm text-gray-500">Products: 22 (-3)</div>
                      </div>
                    </div>
                  </td>
                  {/* Week 4 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺9,800</div>
                      <div className="text-sm text-gray-500">Conversion: 1.2%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 5,200</div>
                        <div className="text-sm text-red-600 font-medium">(3.5%)</div>
                        <div className="text-sm text-gray-500">RPV: ₺1.88</div>
                        <div className="text-sm text-gray-500">Products: 18 (-4)</div>
                      </div>
                    </div>
                  </td>
                  {/* Dogs - Week 5 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺8,500</div>
                      <div className="text-sm text-gray-500">Conversion: 1.1%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 4,200</div>
                        <div className="text-sm text-red-600 font-medium">(2.8%)</div>
                        <div className="text-sm text-gray-500">RPV: ₺2.02</div>
                        <div className="text-sm text-gray-500">Products: 15 (-3)</div>
                      </div>
                    </div>
                  </td>
                  {/* Dogs - Week 6 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺7,200</div>
                      <div className="text-sm text-gray-500">Conversion: 1.0%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 3,500</div>
                        <div className="text-sm text-red-600 font-medium">(2.1%)</div>
                        <div className="text-sm text-gray-500">RPV: ₺2.06</div>
                        <div className="text-sm text-gray-500">Products: 12 (-3)</div>
                      </div>
                    </div>
                  </td>
                  {/* Dogs - Week 7 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺6,500</div>
                      <div className="text-sm text-gray-500">Conversion: 1.0%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 3,100</div>
                        <div className="text-sm text-red-600 font-medium">(1.9%)</div>
                        <div className="text-sm text-gray-500">RPV: ₺2.10</div>
                        <div className="text-sm text-gray-500">Products: 10 (-2)</div>
                      </div>
                    </div>
                  </td>
                  {/* Dogs - Week 8 */}
                  <td className="px-4 py-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-800 text-base">₺5,800</div>
                      <div className="text-sm text-gray-500">Conversion: 0.9%</div>
                      <div className="mt-1 pt-1 border-t border-dashed border-gray-200">
                        <div className="text-sm text-gray-500">Views: 2,700</div>
                        <div className="text-sm text-red-600 font-medium">(1.8%)</div>
                        <div className="text-sm text-gray-500">RPV: ₺2.15</div>
                        <div className="text-sm text-gray-500">Products: 8 (-2)</div>
                      </div>
                    </div>
                  </td>
                </tr>
                
                {/* Separator Row */}
                <tr className="h-2 bg-gray-100">
                  <td colSpan="9" className="p-0"></td>
                </tr>
                
                {/* Total Row */}
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-4 py-4 text-gray-800 sticky left-0 bg-gray-50 z-10 text-base">Total</td>
                  
                  {/* Week 1 */}
                  <td className="px-4 py-4 text-right text-gray-800">
                    <div className="text-base">₺255,300</div>
                    <div className="text-sm mt-1 pt-1 border-t border-dashed border-gray-300">
                      Views: 119,600 <span className="text-gray-600 font-medium">(100%)</span>
                    </div>
                  </td>

                  {/* Week 2 */}
                  <td className="px-4 py-4 text-right text-gray-800">
                    <div className="text-base">₺276,500</div>
                    <div className="text-sm mt-1 pt-1 border-t border-dashed border-gray-300">
                      Views: 118,500 <span className="text-gray-600 font-medium">(100%)</span>
                    </div>
                  </td>

                  {/* Week 3 */}
                  <td className="px-4 py-4 text-right text-gray-800">
                    <div className="text-base">₺298,800</div>
                    <div className="text-sm mt-1 pt-1 border-t border-dashed border-gray-300">
                      Views: 129,300 <span className="text-gray-600 font-medium">(100%)</span>
                    </div>
                  </td>

                  {/* Week 4 */}
                  <td className="px-4 py-4 text-right text-gray-800">
                    <div className="text-base">₺324,300</div>
                    <div className="text-sm mt-1 pt-1 border-t border-dashed border-gray-300">
                      Views: 141,900 <span className="text-gray-600 font-medium">(100%)</span>
                    </div>
                  </td>

                  {/* Week 5 */}
                  <td className="px-4 py-4 text-right text-gray-800">
                    <div className="text-base">₺332,000</div>
                    <div className="text-sm mt-1 pt-1 border-t border-dashed border-gray-300">
                      Views: 145,300 <span className="text-gray-600 font-medium">(100%)</span>
                    </div>
                  </td>

                  {/* Week 6 */}
                  <td className="px-4 py-4 text-right text-gray-800">
                    <div className="text-base">₺338,700</div>
                    <div className="text-sm mt-1 pt-1 border-t border-dashed border-gray-300">
                      Views: 149,500 <span className="text-gray-600 font-medium">(100%)</span>
                    </div>
                  </td>

                  {/* Week 7 */}
                  <td className="px-4 py-4 text-right text-gray-800">
                    <div className="text-base">₺346,700</div>
                    <div className="text-sm mt-1 pt-1 border-t border-dashed border-gray-300">
                      Views: 156,200 <span className="text-gray-600 font-medium">(100%)</span>
                    </div>
                  </td>

                  {/* Week 8 */}
                  <td className="px-4 py-4 text-right text-gray-800">
                    <div className="text-base">₺351,900</div>
                    <div className="text-sm mt-1 pt-1 border-t border-dashed border-gray-300">
                      Views: 163,800 <span className="text-gray-600 font-medium">(100%)</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Customer Segmentation Analysis Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200 mt-8">
          {/* Header kısmı */}
          <div className="bg-gradient-to-r from-purple-800 to-violet-900 text-white p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h3 className="text-base sm:text-lg font-semibold">Customer Segmentation Analysis</h3>
                <p className="text-xs sm:text-sm text-purple-100 mt-1">Customer behavior and spending patterns across different segments</p>
              </div>
              <span className="inline-flex items-center px-3 py-1 bg-blue-500 text-white text-xs sm:text-sm rounded-full gap-1 whitespace-nowrap">
                <ArrowUpRight className="w-3.5 h-3.5" />
                <span>LTV Growth: +18.5%</span>
              </span>
            </div>
          </div>
          
          {/* Table container */}
          <div className="relative overflow-x-auto">
            <table className="w-full" style={{ minWidth: '1200px' }}>
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 w-[200px]">SEGMENT</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[140px]">CUSTOMERS</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[140px]">AVG ORDER VALUE</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[140px]">FREQUENCY</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[140px]">LTV</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[140px]">REVENUE</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[140px]">RETENTION</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[140px]">CLV TREND</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* VIP Customers */}
                <tr className="hover:bg-purple-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-purple-50">
                                         <div className="flex items-center space-x-3">
                       <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                         <span className="text-purple-600 text-xl font-bold">V</span>
                       </div>
                       <div className="flex flex-col">
                         <span className="font-medium text-gray-900 text-base">VIP Customers</span>
                         <span className="text-gray-500 text-sm">Premium Loyalists</span>
                       </div>
                     </div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">2,847</div>
                    <div className="text-sm text-purple-600">(8.2% of total)</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">₺2,485</div>
                    <div className="text-sm text-gray-500">+15.3% vs prev</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">8.4/year</div>
                    <div className="text-sm text-gray-500">Every 43 days</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">₺18,650</div>
                    <div className="text-sm text-green-600">+22.1% growth</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">₺5.9M</div>
                    <div className="text-sm text-purple-600">(42.3% of total)</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">94.2%</div>
                    <div className="text-sm text-gray-500">24-month</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                      <ArrowUpRight className="w-3 h-3" />
                      <span>+22%</span>
                    </div>
                  </td>
                </tr>

                {/* Regular Customers */}
                <tr className="hover:bg-blue-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-blue-50">
                                         <div className="flex items-center space-x-3">
                       <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                         <span className="text-blue-600 text-xl font-bold">R</span>
                       </div>
                       <div className="flex flex-col">
                         <span className="font-medium text-gray-900 text-base">Regular Customers</span>
                         <span className="text-gray-500 text-sm">Steady Buyers</span>
                       </div>
                     </div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">8,935</div>
                    <div className="text-sm text-blue-600">(25.7% of total)</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">₺945</div>
                    <div className="text-sm text-gray-500">+8.2% vs prev</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">4.2/year</div>
                    <div className="text-sm text-gray-500">Every 87 days</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">₺3,969</div>
                    <div className="text-sm text-green-600">+12.4% growth</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">₺3.5M</div>
                    <div className="text-sm text-blue-600">(25.1% of total)</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">76.8%</div>
                    <div className="text-sm text-gray-500">18-month</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                      <ArrowUpRight className="w-3 h-3" />
                      <span>+12%</span>
                    </div>
                  </td>
                </tr>

                {/* Occasional Buyers */}
                <tr className="hover:bg-yellow-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-yellow-50">
                                         <div className="flex items-center space-x-3">
                       <div className="flex-shrink-0 w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                         <span className="text-yellow-600 text-xl font-bold">O</span>
                       </div>
                       <div className="flex flex-col">
                         <span className="font-medium text-gray-900 text-base">Occasional Buyers</span>
                         <span className="text-gray-500 text-sm">Seasonal Shoppers</span>
                       </div>
                     </div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">12,458</div>
                    <div className="text-sm text-yellow-600">(35.8% of total)</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">₺567</div>
                    <div className="text-sm text-gray-500">+3.1% vs prev</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">2.1/year</div>
                    <div className="text-sm text-gray-500">Every 174 days</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">₺1,191</div>
                    <div className="text-sm text-green-600">+5.8% growth</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">₺1.8M</div>
                    <div className="text-sm text-yellow-600">(12.9% of total)</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">45.3%</div>
                    <div className="text-sm text-gray-500">12-month</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm">
                      <ArrowUpRight className="w-3 h-3" />
                      <span>+6%</span>
                    </div>
                  </td>
                </tr>

                {/* One-Time Buyers */}
                <tr className="hover:bg-gray-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-gray-50">
                                         <div className="flex items-center space-x-3">
                       <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                         <span className="text-gray-600 text-xl font-bold">1</span>
                       </div>
                       <div className="flex flex-col">
                         <span className="font-medium text-gray-900 text-base">One-Time Buyers</span>
                         <span className="text-gray-500 text-sm">Single Purchase</span>
                       </div>
                     </div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">10,547</div>
                    <div className="text-sm text-gray-600">(30.3% of total)</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">₺387</div>
                    <div className="text-sm text-gray-500">-2.1% vs prev</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">1.0/year</div>
                    <div className="text-sm text-gray-500">Single purchase</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">₺387</div>
                    <div className="text-sm text-red-600">-2.1% decline</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">₺2.7M</div>
                    <div className="text-sm text-gray-600">(19.4% of total)</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">0%</div>
                    <div className="text-sm text-gray-500">No return</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-sm">
                      <ArrowDown className="w-3 h-3" />
                      <span>-2%</span>
                    </div>
                  </td>
                </tr>

                {/* Separator Row */}
                <tr className="h-2 bg-gray-100">
                  <td colSpan="8" className="p-0"></td>
                </tr>

                {/* Total Row */}
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-4 py-4 text-gray-800 sticky left-0 bg-gray-50 z-10 text-base">TOTAL</td>
                  <td className="px-3 py-4 text-right text-gray-800">
                    <div className="text-base">34,787</div>
                    <div className="text-sm text-gray-600">Total customers</div>
                  </td>
                  <td className="px-3 py-4 text-right text-gray-800">
                    <div className="text-base">₺847</div>
                    <div className="text-sm text-gray-600">Avg across all</div>
                  </td>
                  <td className="px-3 py-4 text-right text-gray-800">
                    <div className="text-base">3.8/year</div>
                    <div className="text-sm text-gray-600">Weighted avg</div>
                  </td>
                  <td className="px-3 py-4 text-right text-gray-800">
                    <div className="text-base">₺3,219</div>
                    <div className="text-sm text-green-600">+11.2% growth</div>
                  </td>
                  <td className="px-3 py-4 text-right text-gray-800">
                    <div className="text-base">₺13.9M</div>
                    <div className="text-sm text-gray-600">Total revenue</div>
                  </td>
                  <td className="px-3 py-4 text-right text-gray-800">
                    <div className="text-base">54.1%</div>
                    <div className="text-sm text-gray-600">Overall retention</div>
                  </td>
                  <td className="px-3 py-4 text-right text-gray-800">
                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                      <ArrowUpRight className="w-3 h-3" />
                      <span>+11%</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Customer Segmentation Insights */}
          <div className="p-6 bg-gray-50 border-t">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                             <div className="bg-white rounded-lg p-4 border">
                 <div className="flex items-center gap-3 mb-2">
                   <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                     <span className="text-purple-600 font-bold">V</span>
                   </div>
                   <h4 className="font-medium text-gray-800">VIP Strategy</h4>
                 </div>
                 <p className="text-sm text-gray-600">Premium experience, exclusive offers, personalized service</p>
               </div>
               
               <div className="bg-white rounded-lg p-4 border">
                 <div className="flex items-center gap-3 mb-2">
                   <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                     <span className="text-blue-600 font-bold">R</span>
                   </div>
                   <h4 className="font-medium text-gray-800">Regular Engagement</h4>
                 </div>
                 <p className="text-sm text-gray-600">Loyalty programs, regular newsletters, seasonal promotions</p>
               </div>
               
               <div className="bg-white rounded-lg p-4 border">
                 <div className="flex items-center gap-3 mb-2">
                   <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                     <span className="text-yellow-600 font-bold">O</span>
                   </div>
                   <h4 className="font-medium text-gray-800">Re-engagement</h4>
                 </div>
                 <p className="text-sm text-gray-600">Targeted campaigns, special discounts, win-back offers</p>
               </div>
               
               <div className="bg-white rounded-lg p-4 border">
                 <div className="flex items-center gap-3 mb-2">
                   <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                     <span className="text-gray-600 font-bold">1</span>
                   </div>
                   <h4 className="font-medium text-gray-800">Conversion Focus</h4>
                 </div>
                 <p className="text-sm text-gray-600">Follow-up emails, product recommendations, incentives</p>
               </div>
                         </div>
           </div>
         </div>

        {/* Customer Journey Mapping Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200 mt-8">
          {/* Header kısmı */}
          <div className="bg-[#9A80CA] text-white p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h3 className="text-base sm:text-lg font-semibold">Customer Journey Mapping</h3>
                <p className="text-xs sm:text-sm text-purple-100 mt-1">End-to-end customer experience touchpoints and conversion metrics</p>
              </div>
              <span className="inline-flex items-center px-3 py-1 bg-white/20 text-white text-xs sm:text-sm rounded-full gap-1 whitespace-nowrap">
                <ArrowUpRight className="w-3.5 h-3.5" />
                <span>Journey Completion: 78.3%</span>
              </span>
            </div>
          </div>
          
          {/* Table container */}
          <div className="relative overflow-x-auto">
            <table className="w-full" style={{ minWidth: '1400px' }}>
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 w-[180px]">JOURNEY STAGE</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[120px]">USERS</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[120px]">CONVERSION %</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[120px]">AVG TIME</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[120px]">TOUCHPOINTS</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[120px]">SATISFACTION</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[120px]">DROP-OFF %</th>
                  <th className="px-3 py-3 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider w-[200px]">KEY ACTIONS</th>
                  <th className="px-3 py-3 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider w-[180px]">OPTIMIZATION</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* Awareness Stage */}
                <tr className="hover:bg-blue-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-blue-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-blue-600 text-lg font-bold">A</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Awareness</span>
                        <span className="text-gray-500 text-sm">First Contact</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">45,892</div>
                    <div className="text-sm text-blue-600">Entry point</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">100%</div>
                    <div className="text-sm text-gray-500">Starting point</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">0:00</div>
                    <div className="text-sm text-gray-500">Initial touch</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">3.2</div>
                    <div className="text-sm text-gray-500">Avg per user</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">N/A</div>
                    <div className="text-sm text-gray-500">Not measured</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-red-600 text-base">15.2%</div>
                    <div className="text-sm text-gray-500">Immediate exit</div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-700">
                      <div>• Social media ads view</div>
                      <div>• Search result click</div>
                      <div>• Website landing</div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-600">
                      Improve ad targeting, landing page optimization
                    </div>
                  </td>
                </tr>

                {/* Interest Stage */}
                <tr className="hover:bg-green-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-green-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <span className="text-green-600 text-lg font-bold">I</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Interest</span>
                        <span className="text-gray-500 text-sm">Content Engagement</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">38,920</div>
                    <div className="text-sm text-green-600">84.8% progression</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">84.8%</div>
                    <div className="text-sm text-gray-500">From awareness</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">2:35</div>
                    <div className="text-sm text-gray-500">Page time</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">5.7</div>
                    <div className="text-sm text-gray-500">Page views</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">7.2/10</div>
                    <div className="text-sm text-gray-500">Content rating</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-red-600 text-base">22.5%</div>
                    <div className="text-sm text-gray-500">Bounce rate</div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-700">
                      <div>• Product browsing</div>
                      <div>• Content reading</div>
                      <div>• Category exploration</div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-600">
                      Enhance product descriptions, add videos
                    </div>
                  </td>
                </tr>

                {/* Consideration Stage */}
                <tr className="hover:bg-yellow-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-yellow-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                        <span className="text-yellow-600 text-lg font-bold">C</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Consideration</span>
                        <span className="text-gray-500 text-sm">Product Evaluation</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">30,163</div>
                    <div className="text-sm text-yellow-600">77.5% progression</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">77.5%</div>
                    <div className="text-sm text-gray-500">From interest</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">8:42</div>
                    <div className="text-sm text-gray-500">Decision time</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">8.3</div>
                    <div className="text-sm text-gray-500">Comparisons</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">8.1/10</div>
                    <div className="text-sm text-gray-500">Product rating</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-red-600 text-base">35.8%</div>
                    <div className="text-sm text-gray-500">Decision delay</div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-700">
                      <div>• Product comparison</div>
                      <div>• Reviews reading</div>
                      <div>• Size guide check</div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-600">
                      Add comparison tools, customer reviews
                    </div>
                  </td>
                </tr>

                {/* Intent Stage */}
                <tr className="hover:bg-purple-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-purple-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <span className="text-purple-600 text-lg font-bold">P</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Purchase Intent</span>
                        <span className="text-gray-500 text-sm">Cart Addition</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">19,365</div>
                    <div className="text-sm text-purple-600">64.2% progression</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">64.2%</div>
                    <div className="text-sm text-gray-500">From consideration</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">12:18</div>
                    <div className="text-sm text-gray-500">Cart session</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">4.2</div>
                    <div className="text-sm text-gray-500">Cart items</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">8.4/10</div>
                    <div className="text-sm text-gray-500">Cart experience</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-red-600 text-base">28.7%</div>
                    <div className="text-sm text-gray-500">Cart abandonment</div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-700">
                      <div>• Add to cart</div>
                      <div>• Wishlist saving</div>
                      <div>• Price checking</div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-600">
                      Reduce cart abandonment, exit-intent popups
                    </div>
                  </td>
                </tr>

                {/* Purchase Stage */}
                <tr className="hover:bg-indigo-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-indigo-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                        <span className="text-indigo-600 text-lg font-bold">B</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Purchase</span>
                        <span className="text-gray-500 text-sm">Checkout Complete</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">13,807</div>
                    <div className="text-sm text-indigo-600">71.3% progression</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">71.3%</div>
                    <div className="text-sm text-gray-500">From intent</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">4:27</div>
                    <div className="text-sm text-gray-500">Checkout time</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">2.1</div>
                    <div className="text-sm text-gray-500">Checkout steps</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">8.9/10</div>
                    <div className="text-sm text-gray-500">Checkout rating</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-red-600 text-base">8.2%</div>
                    <div className="text-sm text-gray-500">Payment failure</div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-700">
                      <div>• Payment processing</div>
                      <div>• Order confirmation</div>
                      <div>• Account creation</div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-600">
                      Streamline checkout, multiple payment options
                    </div>
                  </td>
                </tr>

                {/* Post-Purchase Stage */}
                <tr className="hover:bg-green-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-green-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <span className="text-green-600 text-lg font-bold">L</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Loyalty</span>
                        <span className="text-gray-500 text-sm">Post-Purchase</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">12,673</div>
                    <div className="text-sm text-green-600">91.8% satisfaction</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">91.8%</div>
                    <div className="text-sm text-gray-500">Delivery success</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">3.2 days</div>
                    <div className="text-sm text-gray-500">Delivery time</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">2.8</div>
                    <div className="text-sm text-gray-500">Support contacts</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">9.1/10</div>
                    <div className="text-sm text-gray-500">Overall rating</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-red-600 text-base">3.5%</div>
                    <div className="text-sm text-gray-500">Return rate</div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-700">
                      <div>• Order tracking</div>
                      <div>• Delivery confirmation</div>
                      <div>• Review request</div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-600">
                      Loyalty program, personalized offers
                    </div>
                  </td>
                </tr>

                {/* Separator Row */}
                <tr className="h-2 bg-gray-100">
                  <td colSpan="9" className="p-0"></td>
                </tr>

                {/* Total Journey Summary */}
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-4 py-4 text-gray-800 sticky left-0 bg-gray-50 z-10 text-base">JOURNEY SUMMARY</td>
                  <td className="px-3 py-4 text-right text-gray-800">
                    <div className="text-base">45,892</div>
                    <div className="text-sm text-gray-600">Total entries</div>
                  </td>
                  <td className="px-3 py-4 text-right text-gray-800">
                    <div className="text-base">30.1%</div>
                    <div className="text-sm text-gray-600">End-to-end</div>
                  </td>
                  <td className="px-3 py-4 text-right text-gray-800">
                    <div className="text-base">31.2 min</div>
                    <div className="text-sm text-gray-600">Total time</div>
                  </td>
                  <td className="px-3 py-4 text-right text-gray-800">
                    <div className="text-base">26.5</div>
                    <div className="text-sm text-gray-600">Avg touchpoints</div>
                  </td>
                  <td className="px-3 py-4 text-right text-gray-800">
                    <div className="text-base">8.3/10</div>
                    <div className="text-sm text-gray-600">Overall experience</div>
                  </td>
                  <td className="px-3 py-4 text-right text-gray-800">
                    <div className="text-base">18.8%</div>
                    <div className="text-sm text-gray-600">Avg drop-off</div>
                  </td>
                  <td className="px-3 py-4 text-gray-800">
                    <div className="text-sm">Multi-channel touchpoint optimization</div>
                  </td>
                  <td className="px-3 py-4 text-gray-800">
                    <div className="text-sm">Focus on consideration & intent stages</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Journey Insights */}
          <div className="p-6 bg-gray-50 border-t">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg p-4 border">
                <h4 className="font-medium text-gray-800 mb-3">🎯 Key Insights</h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• Highest drop-off at consideration stage (35.8%)</li>
                  <li>• Strong conversion from intent to purchase (71.3%)</li>
                  <li>• Excellent post-purchase satisfaction (9.1/10)</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-lg p-4 border">
                <h4 className="font-medium text-gray-800 mb-3">🚀 Quick Wins</h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• Add product comparison feature</li>
                  <li>• Implement cart abandonment emails</li>
                  <li>• Optimize checkout flow</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-lg p-4 border">
                <h4 className="font-medium text-gray-800 mb-3">📈 Growth Opportunities</h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• Personalized product recommendations</li>
                  <li>• Loyalty program enhancement</li>
                  <li>• Mobile app development</li>
                </ul>
              </div>
                         </div>
           </div>
         </div>

        {/* Customer Lifetime Value (LTV) Analysis */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200 mt-8">
          {/* Header kısmı */}
          <div className="bg-gradient-to-r from-teal-700 to-emerald-800 text-white p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h3 className="text-base sm:text-lg font-semibold">Customer Lifetime Value (LTV) Analysis</h3>
                <p className="text-xs sm:text-sm text-emerald-100 mt-1">Segment bazlı AOV, frekans, LTV, CAC ve geri ödeme süresi</p>
              </div>
              <span className="inline-flex items-center px-3 py-1 bg-white/20 text-white text-xs sm:text-sm rounded-full gap-1 whitespace-nowrap">
                <ArrowUpRight className="w-3.5 h-3.5" />
                <span>Overall LTV Growth: +14.6%</span>
              </span>
            </div>
          </div>

          {/* Table container */}
          <div className="relative overflow-x-auto">
            <table className="w-full" style={{ minWidth: '1200px' }}>
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 w-[220px]">SEGMENT</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[120px]">CUSTOMERS</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[120px]">AOV</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[140px]">PURCH/YEAR</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[140px]">RETENTION</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[140px]">LTV</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[120px]">CAC</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[140px]">PAYBACK</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[120px]">TREND</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* VIP */}
                <tr className="hover:bg-emerald-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-emerald-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                        <span className="text-emerald-700 text-xl font-bold">V</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">VIP</span>
                        <span className="text-gray-500 text-sm">Premium Loyalists</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right"><div className="font-medium text-gray-800">2,847</div></td>
                  <td className="px-3 py-4 text-right"><div className="font-medium text-gray-800">₺2,485</div></td>
                  <td className="px-3 py-4 text-right"><div className="font-medium text-gray-800">8.4</div></td>
                  <td className="px-3 py-4 text-right"><div className="font-medium text-emerald-700">94.2%</div></td>
                  <td className="px-3 py-4 text-right"><div className="font-semibold text-gray-900">₺18,650</div></td>
                  <td className="px-3 py-4 text-right"><div className="font-medium text-gray-800">₺650</div></td>
                  <td className="px-3 py-4 text-right"><div className="font-medium text-emerald-700">1.2 mo</div></td>
                  <td className="px-3 py-4 text-right">
                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs">
                      <ArrowUpRight className="w-3 h-3" />
                      <span>+22%</span>
                    </div>
                  </td>
                </tr>

                {/* Regular */}
                <tr className="hover:bg-blue-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-blue-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-blue-700 text-xl font-bold">R</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Regular</span>
                        <span className="text-gray-500 text-sm">Steady Buyers</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right"><div className="font-medium text-gray-800">8,935</div></td>
                  <td className="px-3 py-4 text-right"><div className="font-medium text-gray-800">₺945</div></td>
                  <td className="px-3 py-4 text-right"><div className="font-medium text-gray-800">4.2</div></td>
                  <td className="px-3 py-4 text-right"><div className="font-medium text-blue-700">76.8%</div></td>
                  <td className="px-3 py-4 text-right"><div className="font-semibold text-gray-900">₺3,969</div></td>
                  <td className="px-3 py-4 text-right"><div className="font-medium text-gray-800">₺280</div></td>
                  <td className="px-3 py-4 text-right"><div className="font-medium text-emerald-700">1.0 mo</div></td>
                  <td className="px-3 py-4 text-right">
                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs">
                      <ArrowUpRight className="w-3 h-3" />
                      <span>+12%</span>
                    </div>
                  </td>
                </tr>

                {/* Occasional */}
                <tr className="hover:bg-yellow-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-yellow-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                        <span className="text-yellow-700 text-xl font-bold">O</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Occasional</span>
                        <span className="text-gray-500 text-sm">Seasonal Shoppers</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right"><div className="font-medium text-gray-800">12,458</div></td>
                  <td className="px-3 py-4 text-right"><div className="font-medium text-gray-800">₺567</div></td>
                  <td className="px-3 py-4 text-right"><div className="font-medium text-gray-800">2.1</div></td>
                  <td className="px-3 py-4 text-right"><div className="font-medium text-yellow-700">45.3%</div></td>
                  <td className="px-3 py-4 text-right"><div className="font-semibold text-gray-900">₺1,191</div></td>
                  <td className="px-3 py-4 text-right"><div className="font-medium text-gray-800">₺160</div></td>
                  <td className="px-3 py-4 text-right"><div className="font-medium text-emerald-700">1.7 mo</div></td>
                  <td className="px-3 py-4 text-right">
                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs">
                      <ArrowUpRight className="w-3 h-3" />
                      <span>+6%</span>
                    </div>
                  </td>
                </tr>

                {/* One-time */}
                <tr className="hover:bg-gray-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-gray-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                        <span className="text-gray-700 text-xl font-bold">1</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">One-time</span>
                        <span className="text-gray-500 text-sm">Single Purchase</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right"><div className="font-medium text-gray-800">10,547</div></td>
                  <td className="px-3 py-4 text-right"><div className="font-medium text-gray-800">₺387</div></td>
                  <td className="px-3 py-4 text-right"><div className="font-medium text-gray-800">1.0</div></td>
                  <td className="px-3 py-4 text-right"><div className="font-medium text-gray-600">0%</div></td>
                  <td className="px-3 py-4 text-right"><div className="font-semibold text-gray-900">₺387</div></td>
                  <td className="px-3 py-4 text-right"><div className="font-medium text-gray-800">₺90</div></td>
                  <td className="px-3 py-4 text-right"><div className="font-medium text-red-600">N/A</div></td>
                  <td className="px-3 py-4 text-right">
                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">
                      <ArrowDown className="w-3 h-3" />
                      <span>-2%</span>
                    </div>
                  </td>
                </tr>

                {/* Total */}
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-4 py-4 text-gray-800 sticky left-0 bg-gray-50 z-10">TOTAL</td>
                  <td className="px-3 py-4 text-right text-gray-800"><div>34,787</div></td>
                  <td className="px-3 py-4 text-right text-gray-800"><div>₺847</div></td>
                  <td className="px-3 py-4 text-right text-gray-800"><div>3.8</div></td>
                  <td className="px-3 py-4 text-right text-gray-800"><div>54.1%</div></td>
                  <td className="px-3 py-4 text-right text-gray-800"><div>₺3,219</div></td>
                  <td className="px-3 py-4 text-right text-gray-800"><div>₺210</div></td>
                  <td className="px-3 py-4 text-right text-gray-800"><div>1.3 mo</div></td>
                  <td className="px-3 py-4 text-right text-gray-800"><div>+14.6%</div></td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* LTV Insights */}
          <div className="p-6 bg-gray-50 border-t">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg p-4 border">
                <h4 className="font-medium text-gray-800 mb-2">📈 Formula & Notes</h4>
                <p className="text-sm text-gray-600">
                  LTV ≈ AOV × Purchase Frequency × Retention Multiplier. Payback = CAC / (AOV × Frequency / 12)
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 border">
                <h4 className="font-medium text-gray-800 mb-2">🎯 Actions</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• VIP: Upsell paketleri, özel kampanyalar</li>
                  <li>• Regular: Sadakat ve referans programları</li>
                  <li>• Occasional: Win-back e-posta ve kupon</li>
                </ul>
              </div>
              <div className="bg-white rounded-lg p-4 border">
                <h4 className="font-medium text-gray-800 mb-2">🧪 Experiments</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• AOV artırımı için bundle testleri</li>
                  <li>• Frekans için abonelik modülleri</li>
                  <li>• CAC düşürmek için kanallar arası optimizasyon</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* RFM Analysis */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200 mt-8">
          {/* Header */}
          <div className="bg-gradient-to-r from-fuchsia-700 to-pink-700 text-white p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h3 className="text-base sm:text-lg font-semibold">RFM Analysis (Recency • Frequency • Monetary)</h3>
                <p className="text-xs sm:text-sm text-pink-100 mt-1">Customer segmentation by last purchase, purchase frequency, and spend</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="hidden sm:inline text-xs text-white/80">Mode:</span>
                <div className="inline-flex bg-white/15 rounded-md p-0.5">
                  <button
                    onClick={() => setRfMode(true)}
                    className={`${rfMode ? 'bg-white text-pink-700' : 'text-white/90'} text-xs px-2.5 py-1 rounded`}
                  >
                    RF Only
                  </button>
                  <button
                    onClick={() => setRfMode(false)}
                    className={`${!rfMode ? 'bg-white text-pink-700' : 'text-white/90'} text-xs px-2.5 py-1 rounded`}
                  >
                    RFM
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="relative overflow-x-auto">
            <table className="w-full" style={{ minWidth: '1200px' }}>
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 w-[220px]">SEGMENT</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[120px]">RECENCY</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[140px]">FREQUENCY</th>
                  {!rfMode && (
                    <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[140px]">MONETARY</th>
                  )}
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[120px]">CUSTOMERS</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[140px]">REVENUE SHARE</th>
                  <th className="px-3 py-3 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">RECOMMENDED ACTIONS</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* Champions */}
                <tr className="hover:bg-emerald-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-emerald-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                        <span className="text-emerald-700 text-xl font-bold">🏆</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Champions</span>
                        <span className="text-gray-500 text-sm">R: 0-30d • F: 10+ • M: High</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right"><div className="text-gray-800">12 days</div></td>
                  <td className="px-3 py-4 text-right"><div className="text-gray-800">12.4/yr</div></td>
                  {!rfMode && (<td className="px-3 py-4 text-right"><div className="text-gray-900 font-semibold">₺2,750</div></td>)}
                  <td className="px-3 py-4 text-right"><div className="text-gray-800">2,145</div></td>
                  <td className="px-3 py-4 text-right"><div className="text-emerald-700 font-medium">34.8%</div></td>
                  <td className="px-3 py-4 text-left text-sm text-gray-700">VIP program, erken erişim, premium paketler</td>
                </tr>

                {/* Loyal Customers */}
                <tr className="hover:bg-blue-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-blue-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-blue-700 text-xl font-bold">💙</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Loyal Customers</span>
                        <span className="text-gray-500 text-sm">R: 0-60d • F: 5-10 • M: Mid/High</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right"><div className="text-gray-800">27 days</div></td>
                  <td className="px-3 py-4 text-right"><div className="text-gray-800">6.8/yr</div></td>
                  {!rfMode && (<td className="px-3 py-4 text-right"><div className="text-gray-900 font-semibold">₺1,150</div></td>)}
                  <td className="px-3 py-4 text-right"><div className="text-gray-800">6,432</div></td>
                  <td className="px-3 py-4 text-right"><div className="text-blue-700 font-medium">28.4%</div></td>
                  <td className="px-3 py-4 text-left text-sm text-gray-700">Loyalty rewards, referral incentives, cross-sell</td>
                </tr>

                {/* Potential Loyalists */}
                <tr className="hover:bg-indigo-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-indigo-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                        <span className="text-indigo-700 text-xl font-bold">🌟</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Potential Loyalists</span>
                        <span className="text-gray-500 text-sm">R: 0-30d • F: 2-4 • M: Mid</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right"><div className="text-gray-800">9 days</div></td>
                  <td className="px-3 py-4 text-right"><div className="text-gray-800">2.9/yr</div></td>
                  {!rfMode && (<td className="px-3 py-4 text-right"><div className="text-gray-900 font-semibold">₺780</div></td>)}
                  <td className="px-3 py-4 text-right"><div className="text-gray-800">8,921</div></td>
                  <td className="px-3 py-4 text-right"><div className="text-indigo-700 font-medium">18.3%</div></td>
                  <td className="px-3 py-4 text-left text-sm text-gray-700">Welcome series, subscription offers, bundle promos</td>
                </tr>

                {/* New Customers */}
                <tr className="hover:bg-violet-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-violet-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
                        <span className="text-violet-700 text-xl font-bold">🆕</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">New Customers</span>
                        <span className="text-gray-500 text-sm">R: 0-15d • F: 1 • M: Low/Mid</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right"><div className="text-gray-800">4 days</div></td>
                  <td className="px-3 py-4 text-right"><div className="text-gray-800">1.0/yr</div></td>
                  {!rfMode && (<td className="px-3 py-4 text-right"><div className="text-gray-900 font-semibold">₺420</div></td>)}
                  <td className="px-3 py-4 text-right"><div className="text-gray-800">12,347</div></td>
                  <td className="px-3 py-4 text-right"><div className="text-violet-700 font-medium">9.1%</div></td>
                  <td className="px-3 py-4 text-left text-sm text-gray-700">Onboarding, post-first-purchase suggestions, small coupon</td>
                </tr>

                {/* At Risk */}
                <tr className="hover:bg-orange-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-orange-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                        <span className="text-orange-700 text-xl font-bold">⚠️</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">At Risk</span>
                        <span className="text-gray-500 text-sm">R: 60-120d • F: 2-4 • M: Mid</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right"><div className="text-gray-800">87 days</div></td>
                  <td className="px-3 py-4 text-right"><div className="text-gray-800">2.3/yr</div></td>
                  {!rfMode && (<td className="px-3 py-4 text-right"><div className="text-gray-900 font-semibold">₺690</div></td>)}
                  <td className="px-3 py-4 text-right"><div className="text-gray-800">5,612</div></td>
                  <td className="px-3 py-4 text-right"><div className="text-orange-700 font-medium">6.8%</div></td>
                  <td className="px-3 py-4 text-left text-sm text-gray-700">Win-back campaigns, limited-time offers, reminders</td>
                </tr>

                {/* Can't Lose Them */}
                <tr className="hover:bg-red-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-red-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                        <span className="text-red-700 text-xl font-bold">❗</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Can’t Lose Them</span>
                        <span className="text-gray-500 text-sm">R: 120-365d • F: 5+ • M: High</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right"><div className="text-gray-800">193 days</div></td>
                  <td className="px-3 py-4 text-right"><div className="text-gray-800">5.6/yr</div></td>
                  {!rfMode && (<td className="px-3 py-4 text-right"><div className="text-gray-900 font-semibold">₺1,980</div></td>)}
                  <td className="px-3 py-4 text-right"><div className="text-gray-800">1,238</div></td>
                  <td className="px-3 py-4 text-right"><div className="text-red-700 font-medium">7.6%</div></td>
                  <td className="px-3 py-4 text-left text-sm text-gray-700">Special comeback offers, personal outreach, VIP win-back</td>
                </tr>

                {/* Hibernating */}
                <tr className="hover:bg-gray-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-gray-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                        <span className="text-gray-700 text-xl font-bold">🛌</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Hibernating</span>
                        <span className="text-gray-500 text-sm">R: 365+d • F: 1-2 • M: Low</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right"><div className="text-gray-800">427 days</div></td>
                  <td className="px-3 py-4 text-right"><div className="text-gray-800">1.2/yr</div></td>
                  {!rfMode && (<td className="px-3 py-4 text-right"><div className="text-gray-900 font-semibold">₺290</div></td>)}
                  <td className="px-3 py-4 text-right"><div className="text-gray-800">9,804</div></td>
                  <td className="px-3 py-4 text-right"><div className="text-gray-700 font-medium">4.0%</div></td>
                  <td className="px-3 py-4 text-left text-sm text-gray-700">Re-activation campaigns, seasonal coupons, low threshold</td>
                </tr>

                {/* Total */}
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-4 py-4 text-gray-800 sticky left-0 bg-gray-50 z-10">TOTAL</td>
                  <td className="px-3 py-4 text-right text-gray-800"><div>Avg 78d</div></td>
                  <td className="px-3 py-4 text-right text-gray-800"><div>3.9/yr</div></td>
                  {!rfMode && (<td className="px-3 py-4 text-right text-gray-800"><div>₺872</div></td>)}
                  <td className="px-3 py-4 text-right text-gray-800"><div>36,497</div></td>
                  <td className="px-3 py-4 text-right text-gray-800"><div>100%</div></td>
                  <td className="px-3 py-4 text-left text-gray-800"><div>Segment-based automation and offer matrices</div></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Website Behavior Analysis Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200 mt-8">
          {/* Header kısmı */}
          <div className="bg-gradient-to-r from-indigo-800 to-blue-900 text-white p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h3 className="text-base sm:text-lg font-semibold">Website Behavior Analysis</h3>
                <p className="text-xs sm:text-sm text-indigo-100 mt-1">User interaction patterns and engagement metrics across website sections</p>
              </div>
              <span className="inline-flex items-center px-3 py-1 bg-white/20 text-white text-xs sm:text-sm rounded-full gap-1 whitespace-nowrap">
                <ArrowUpRight className="w-3.5 h-3.5" />
                <span>Engagement Score: 7.8/10</span>
              </span>
            </div>
          </div>
          
          {/* Table container */}
          <div className="relative overflow-x-auto">
            <table className="w-full" style={{ minWidth: '1500px' }}>
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 w-[200px]">PAGE SECTION</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[120px]">PAGEVIEWS</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[120px]">AVG TIME</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[120px]">BOUNCE RATE</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[120px]">SCROLL DEPTH</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[120px]">CLICK RATE</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[120px]">EXIT RATE</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[120px]">CONVERSION</th>
                  <th className="px-3 py-3 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider w-[180px]">USER ACTIONS</th>
                  <th className="px-3 py-3 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider w-[200px]">OPTIMIZATION NOTES</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* Homepage */}
                <tr className="hover:bg-blue-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-blue-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-blue-600 text-lg font-bold">H</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Homepage</span>
                        <span className="text-gray-500 text-sm">Landing Page</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">128,547</div>
                    <div className="text-sm text-blue-600">35.2% of total</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">1:45</div>
                    <div className="text-sm text-gray-500">+12% vs prev</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-red-600 text-base">42.3%</div>
                    <div className="text-sm text-gray-500">High bounce</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">68%</div>
                    <div className="text-sm text-gray-500">Above fold</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">15.2%</div>
                    <div className="text-sm text-gray-500">CTA clicks</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">38.7%</div>
                    <div className="text-sm text-gray-500">Exit point</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">2.8%</div>
                    <div className="text-sm text-green-600">To purchase</div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-700">
                      <div>• Hero banner view</div>
                      <div>• Navigation clicks</div>
                      <div>• Search usage</div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-600">
                      Improve hero content, reduce bounce rate
                    </div>
                  </td>
                </tr>

                {/* Product Category Pages */}
                <tr className="hover:bg-green-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-green-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <span className="text-green-600 text-lg font-bold">C</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Category Pages</span>
                        <span className="text-gray-500 text-sm">Product Listings</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">89,234</div>
                    <div className="text-sm text-green-600">24.4% of total</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">3:22</div>
                    <div className="text-sm text-gray-500">+8% vs prev</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-yellow-600 text-base">28.5%</div>
                    <div className="text-sm text-gray-500">Moderate</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">84%</div>
                    <div className="text-sm text-gray-500">Full scroll</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">22.7%</div>
                    <div className="text-sm text-gray-500">Product clicks</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">25.3%</div>
                    <div className="text-sm text-gray-500">Category exit</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">4.2%</div>
                    <div className="text-sm text-green-600">Good flow</div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-700">
                      <div>• Filter usage</div>
                      <div>• Sort options</div>
                      <div>• Product clicks</div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-600">
                      Enhance filtering, add quick view
                    </div>
                  </td>
                </tr>

                {/* Product Detail Pages */}
                <tr className="hover:bg-purple-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-purple-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <span className="text-purple-600 text-lg font-bold">P</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Product Pages</span>
                        <span className="text-gray-500 text-sm">Detail Views</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">67,891</div>
                    <div className="text-sm text-purple-600">18.6% of total</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">5:18</div>
                    <div className="text-sm text-gray-500">+15% vs prev</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-green-600 text-base">18.2%</div>
                    <div className="text-sm text-gray-500">Low bounce</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">92%</div>
                    <div className="text-sm text-gray-500">Deep scroll</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">35.4%</div>
                    <div className="text-sm text-gray-500">Add to cart</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">15.8%</div>
                    <div className="text-sm text-gray-500">Low exit</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">8.7%</div>
                    <div className="text-sm text-green-600">Strong convert</div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-700">
                      <div>• Image gallery</div>
                      <div>• Size selection</div>
                      <div>• Reviews reading</div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-600">
                      Add more product videos, size guide
                    </div>
                  </td>
                </tr>

                {/* Shopping Cart */}
                <tr className="hover:bg-yellow-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-yellow-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                        <span className="text-yellow-600 text-lg font-bold">S</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Shopping Cart</span>
                        <span className="text-gray-500 text-sm">Cart Management</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">23,456</div>
                    <div className="text-sm text-yellow-600">6.4% of total</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">2:47</div>
                    <div className="text-sm text-gray-500">Decision time</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-red-600 text-base">45.8%</div>
                    <div className="text-sm text-gray-500">Cart abandon</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">76%</div>
                    <div className="text-sm text-gray-500">Page review</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">42.3%</div>
                    <div className="text-sm text-gray-500">Proceed click</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">32.1%</div>
                    <div className="text-sm text-gray-500">Cart exit</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">54.2%</div>
                    <div className="text-sm text-green-600">To checkout</div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-700">
                      <div>• Quantity changes</div>
                      <div>• Item removal</div>
                      <div>• Coupon entry</div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-600">
                      Add trust badges, shipping calculator
                    </div>
                  </td>
                </tr>

                {/* Checkout Process */}
                <tr className="hover:bg-indigo-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-indigo-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                        <span className="text-indigo-600 text-lg font-bold">O</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Checkout</span>
                        <span className="text-gray-500 text-sm">Payment Process</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">12,734</div>
                    <div className="text-sm text-indigo-600">3.5% of total</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">4:32</div>
                    <div className="text-sm text-gray-500">Form filling</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-red-600 text-base">12.5%</div>
                    <div className="text-sm text-gray-500">Payment exit</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">95%</div>
                    <div className="text-sm text-gray-500">Form focus</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">78.9%</div>
                    <div className="text-sm text-gray-500">Submit attempt</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">8.2%</div>
                    <div className="text-sm text-gray-500">Payment fail</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">87.5%</div>
                    <div className="text-sm text-green-600">Success rate</div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-700">
                      <div>• Form completion</div>
                      <div>• Payment method</div>
                      <div>• Order review</div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-600">
                      Simplify forms, guest checkout option
                    </div>
                  </td>
                </tr>

                {/* Account/Profile Pages */}
                <tr className="hover:bg-gray-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-gray-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                        <span className="text-gray-600 text-lg font-bold">A</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Account Pages</span>
                        <span className="text-gray-500 text-sm">User Profile</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">18,923</div>
                    <div className="text-sm text-gray-600">5.2% of total</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">3:45</div>
                    <div className="text-sm text-gray-500">Profile mgmt</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-green-600 text-base">8.3%</div>
                    <div className="text-sm text-gray-500">Low bounce</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">88%</div>
                    <div className="text-sm text-gray-500">Full engage</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">45.7%</div>
                    <div className="text-sm text-gray-500">Feature usage</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">12.4%</div>
                    <div className="text-sm text-gray-500">Session end</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">15.6%</div>
                    <div className="text-sm text-green-600">Repeat visit</div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-700">
                      <div>• Order history</div>
                      <div>• Profile updates</div>
                      <div>• Preferences</div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-600">
                      Add wishlist, loyalty rewards display
                    </div>
                  </td>
                </tr>

                {/* Support/Help Pages */}
                <tr className="hover:bg-orange-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-orange-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                        <span className="text-orange-600 text-lg font-bold">Q</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Support Pages</span>
                        <span className="text-gray-500 text-sm">Help & FAQ</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">24,567</div>
                    <div className="text-sm text-orange-600">6.7% of total</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">2:18</div>
                    <div className="text-sm text-gray-500">Quick help</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-yellow-600 text-base">35.7%</div>
                    <div className="text-sm text-gray-500">Issue solve</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">65%</div>
                    <div className="text-sm text-gray-500">Scanning</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">28.4%</div>
                    <div className="text-sm text-gray-500">FAQ clicks</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">42.3%</div>
                    <div className="text-sm text-gray-500">Help complete</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">18.9%</div>
                    <div className="text-sm text-green-600">Problem solved</div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-700">
                      <div>• FAQ search</div>
                      <div>• Contact form</div>
                      <div>• Live chat</div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-600">
                      Improve search, add chatbot
                    </div>
                  </td>
                </tr>

                {/* Separator Row */}
                <tr className="h-2 bg-gray-100">
                  <td colSpan="10" className="p-0"></td>
                </tr>

                {/* Overall Summary */}
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-4 py-4 text-gray-800 sticky left-0 bg-gray-50 z-10 text-base">WEBSITE TOTAL</td>
                  <td className="px-3 py-4 text-right text-gray-800">
                    <div className="text-base">365,352</div>
                    <div className="text-sm text-gray-600">Total pageviews</div>
                  </td>
                  <td className="px-3 py-4 text-right text-gray-800">
                    <div className="text-base">3:12</div>
                    <div className="text-sm text-gray-600">Avg session</div>
                  </td>
                  <td className="px-3 py-4 text-right text-gray-800">
                    <div className="text-base">27.8%</div>
                    <div className="text-sm text-gray-600">Overall bounce</div>
                  </td>
                  <td className="px-3 py-4 text-right text-gray-800">
                    <div className="text-base">78%</div>
                    <div className="text-sm text-gray-600">Avg scroll</div>
                  </td>
                  <td className="px-3 py-4 text-right text-gray-800">
                    <div className="text-base">31.2%</div>
                    <div className="text-sm text-gray-600">Click-through</div>
                  </td>
                  <td className="px-3 py-4 text-right text-gray-800">
                    <div className="text-base">24.6%</div>
                    <div className="text-sm text-gray-600">Exit rate</div>
                  </td>
                  <td className="px-3 py-4 text-right text-gray-800">
                    <div className="text-base">15.4%</div>
                    <div className="text-sm text-gray-600">Site conversion</div>
                  </td>
                  <td className="px-3 py-4 text-gray-800">
                    <div className="text-sm">Cross-page navigation optimization</div>
                  </td>
                  <td className="px-3 py-4 text-gray-800">
                    <div className="text-sm">Focus on homepage & cart optimization</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Behavior Insights */}
          <div className="p-6 bg-gray-50 border-t">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg p-4 border">
                <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  Critical Issues
                </h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• High cart abandonment (45.8%)</li>
                  <li>• Homepage bounce rate (42.3%)</li>
                  <li>• Support page inefficiency</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-lg p-4 border">
                <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  Strong Performers
                </h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• Product pages engagement (92% scroll)</li>
                  <li>• Checkout completion (87.5%)</li>
                  <li>• Account page retention</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-lg p-4 border">
                <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  Opportunities
                </h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• Category page filtering</li>
                  <li>• Product recommendation engine</li>
                  <li>• Mobile experience optimization</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-lg p-4 border">
                <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  User Preferences
                </h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• Visual content preference</li>
                  <li>• Quick decision making</li>
                  <li>• Mobile-first behavior</li>
                </ul>
              </div>
                         </div>
           </div>
         </div>

        {/* Cart Abandonment Analysis Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200 mt-8">
          {/* Header kısmı */}
          <div className="bg-gradient-to-r from-red-600 to-red-800 text-white p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h3 className="text-base sm:text-lg font-semibold">Cart Abandonment Analysis</h3>
                <p className="text-xs sm:text-sm text-red-100 mt-1">Detailed breakdown of shopping cart dropout patterns and recovery opportunities</p>
              </div>
              <span className="inline-flex items-center px-3 py-1 bg-white/20 text-white text-xs sm:text-sm rounded-full gap-1 whitespace-nowrap">
                <ArrowUpRight className="w-3.5 h-3.5" />
                <span>Abandonment Rate: 68.4%</span>
              </span>
            </div>
          </div>
          
          {/* Table container */}
          <div className="relative overflow-x-auto">
            <table className="w-full" style={{ minWidth: '1600px' }}>
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 w-[180px]">ABANDONMENT STAGE</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[120px]">USERS</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[120px]">CART VALUE</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[120px]">DROP RATE</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[120px]">TIME SPENT</th>
                  <th className="px-3 py-3 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider w-[200px]">PRIMARY REASONS</th>
                  <th className="px-3 py-3 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider w-[180px]">USER BEHAVIOR</th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[120px]">RECOVERY RATE</th>
                  <th className="px-3 py-3 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider w-[200px]">RECOVERY ACTIONS</th>
                  <th className="px-3 py-3 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider w-[220px]">OPTIMIZATION STRATEGY</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* Product Page - Add to Cart */}
                <tr className="hover:bg-red-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-red-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-blue-600 text-lg font-bold">1</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Add to Cart Intent</span>
                        <span className="text-gray-500 text-sm">Product Page Exit</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">45,623</div>
                    <div className="text-sm text-blue-600">32.8% of visitors</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">₺285</div>
                    <div className="text-sm text-gray-500">Avg item value</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-red-600 text-base">72.3%</div>
                    <div className="text-sm text-gray-500">Never add to cart</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">2:45</div>
                    <div className="text-sm text-gray-500">Product review</div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-700">
                      <div>• Price concerns (38%)</div>
                      <div>• Size uncertainty (24%)</div>
                      <div>• Browsing intent (22%)</div>
                      <div>• Out of stock (16%)</div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-700">
                      <div>• Image gallery focus</div>
                      <div>• Reviews reading</div>
                      <div>• Size guide check</div>
                      <div>• Price comparison</div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">18.4%</div>
                    <div className="text-sm text-green-600">Return & add</div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-600">
                      <div>• Retargeting ads</div>
                      <div>• Email recommendations</div>
                      <div>• Price drop alerts</div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-600">
                      Enhanced size guide, price transparency, social proof
                    </div>
                  </td>
                </tr>

                {/* Cart Page - Initial Abandonment */}
                <tr className="hover:bg-orange-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-orange-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                        <span className="text-orange-600 text-lg font-bold">2</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Cart Review</span>
                        <span className="text-gray-500 text-sm">Initial Cart Exit</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">18,924</div>
                    <div className="text-sm text-orange-600">Items in cart</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">₺847</div>
                    <div className="text-sm text-gray-500">Avg cart value</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-red-600 text-base">58.7%</div>
                    <div className="text-sm text-gray-500">Cart abandonment</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">1:32</div>
                    <div className="text-sm text-gray-500">Quick review</div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-700">
                      <div>• Shipping costs (42%)</div>
                      <div>• Total price shock (28%)</div>
                      <div>• Still browsing (18%)</div>
                      <div>• Payment security (12%)</div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-700">
                      <div>• Quantity adjustments</div>
                      <div>• Remove items</div>
                      <div>• Shipping calculator</div>
                      <div>• Continue shopping</div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">24.6%</div>
                    <div className="text-sm text-green-600">Within 24h</div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-600">
                      <div>• Cart reminder emails</div>
                      <div>• Free shipping offers</div>
                      <div>• Discount codes</div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-600">
                      Free shipping threshold display, total transparency
                    </div>
                  </td>
                </tr>

                {/* Checkout Information */}
                <tr className="hover:bg-yellow-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-yellow-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                        <span className="text-yellow-600 text-lg font-bold">3</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Checkout Info</span>
                        <span className="text-gray-500 text-sm">Form Abandonment</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">7,821</div>
                    <div className="text-sm text-yellow-600">Checkout start</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">₺1,246</div>
                    <div className="text-sm text-gray-500">Higher intent</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-red-600 text-base">31.4%</div>
                    <div className="text-sm text-gray-500">Form exit</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">3:18</div>
                    <div className="text-sm text-gray-500">Form filling</div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-700">
                      <div>• Complex form (35%)</div>
                      <div>• Account creation (25%)</div>
                      <div>• Delivery options (20%)</div>
                      <div>• Technical issues (20%)</div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-700">
                      <div>• Form field focus</div>
                      <div>• Multiple attempts</div>
                      <div>• Back navigation</div>
                      <div>• Help seeking</div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">42.8%</div>
                    <div className="text-sm text-green-600">Same session</div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-600">
                      <div>• Live chat support</div>
                      <div>• Guest checkout</div>
                      <div>• Auto-save forms</div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-600">
                      Simplify forms, guest checkout, progress indicators
                    </div>
                  </td>
                </tr>

                {/* Payment Page */}
                <tr className="hover:bg-purple-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-purple-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <span className="text-purple-600 text-lg font-bold">4</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Payment Process</span>
                        <span className="text-gray-500 text-sm">Payment Failure</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">5,364</div>
                    <div className="text-sm text-purple-600">Payment attempt</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">₺1,398</div>
                    <div className="text-sm text-gray-500">High intent</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-red-600 text-base">15.8%</div>
                    <div className="text-sm text-gray-500">Payment fail</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">4:42</div>
                    <div className="text-sm text-gray-500">Payment process</div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-700">
                      <div>• Card decline (45%)</div>
                      <div>• Security concerns (25%)</div>
                      <div>• System errors (18%)</div>
                      <div>• 3D Secure issues (12%)</div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-700">
                      <div>• Multiple payment tries</div>
                      <div>• Card switching</div>
                      <div>• Contact support</div>
                      <div>• Method change</div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">67.3%</div>
                    <div className="text-sm text-green-600">Retry payment</div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-600">
                      <div>• Payment retry flow</div>
                      <div>• Alternative methods</div>
                      <div>• Support contact</div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-600">
                      Multiple payment options, clear error messages
                    </div>
                  </td>
                </tr>

                {/* Post-Purchase Abandonment */}
                <tr className="hover:bg-green-50 transition-colors duration-150">
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 hover:bg-green-50">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <span className="text-green-600 text-lg font-bold">5</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-base">Order Confirmation</span>
                        <span className="text-gray-500 text-sm">Last Minute Cancel</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">4,518</div>
                    <div className="text-sm text-green-600">Orders placed</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">₺1,425</div>
                    <div className="text-sm text-gray-500">Completed orders</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-red-600 text-base">5.2%</div>
                    <div className="text-sm text-gray-500">Order cancellation</div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">0:45</div>
                    <div className="text-sm text-gray-500">Post-order</div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-700">
                      <div>• Buyer's remorse (40%)</div>
                      <div>• Found better price (30%)</div>
                      <div>• Delivery time (20%)</div>
                      <div>• Order complexity (10%)</div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-700">
                      <div>• Order review</div>
                      <div>• Cancel request</div>
                      <div>• Support contact</div>
                      <div>• Delivery check</div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="font-medium text-gray-800 text-base">15.6%</div>
                    <div className="text-sm text-green-600">Retention effort</div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-600">
                      <div>• Retention calls</div>
                      <div>• Delivery upgrade</div>
                      <div>• Discount offers</div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm text-gray-600">
                      Clear policies, delivery transparency, order benefits
                    </div>
                  </td>
                </tr>

                {/* Separator Row */}
                <tr className="h-2 bg-gray-100">
                  <td colSpan="10" className="p-0"></td>
                </tr>

                {/* Overall Summary */}
                <tr className="bg-red-50 font-semibold">
                  <td className="px-4 py-4 text-gray-800 sticky left-0 bg-red-50 z-10 text-base">TOTAL ABANDONMENT</td>
                  <td className="px-3 py-4 text-right text-gray-800">
                    <div className="text-base">82,250</div>
                    <div className="text-sm text-gray-600">Lost customers</div>
                  </td>
                  <td className="px-3 py-4 text-right text-gray-800">
                    <div className="text-base">₺96.2M</div>
                    <div className="text-sm text-gray-600">Lost revenue</div>
                  </td>
                  <td className="px-3 py-4 text-right text-gray-800">
                    <div className="text-base">68.4%</div>
                    <div className="text-sm text-gray-600">Overall rate</div>
                  </td>
                  <td className="px-3 py-4 text-right text-gray-800">
                    <div className="text-base">2:32</div>
                    <div className="text-sm text-gray-600">Avg consideration</div>
                  </td>
                  <td className="px-3 py-4 text-gray-800">
                    <div className="text-sm">Multi-stage funnel optimization needed</div>
                  </td>
                  <td className="px-3 py-4 text-gray-800">
                    <div className="text-sm">Cross-stage behavior tracking</div>
                  </td>
                  <td className="px-3 py-4 text-right text-gray-800">
                    <div className="text-base">28.7%</div>
                    <div className="text-sm text-gray-600">Recovery potential</div>
                  </td>
                  <td className="px-3 py-4 text-gray-800">
                    <div className="text-sm">Automated recovery campaigns</div>
                  </td>
                  <td className="px-3 py-4 text-gray-800">
                    <div className="text-sm">Focus on stages 1-3 for maximum impact</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Cart Abandonment Insights */}
          <div className="p-6 bg-gray-50 border-t">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg p-4 border border-red-200">
                <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  Critical Drop Points
                </h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• Product page intent (72.3%)</li>
                  <li>• Cart review stage (58.7%)</li>
                  <li>• Form complexity (31.4%)</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-lg p-4 border border-orange-200">
                <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                  <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                  Revenue Impact
                </h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• ₺96.2M lost annually</li>
                  <li>• ₺1,169 avg lost per user</li>
                  <li>• 28.7% recoverable</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-lg p-4 border border-yellow-200">
                <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  Quick Wins
                </h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• Free shipping transparency</li>
                  <li>• Guest checkout option</li>
                  <li>• Cart reminder emails</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-lg p-4 border border-green-200">
                <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  Recovery Success
                </h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• Payment retry: 67.3%</li>
                  <li>• Same session: 42.8%</li>
                  <li>• 24h window: 24.6%</li>
                </ul>
              </div>
            </div>

            {/* Action Plan */}
            <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-lg p-6 border border-red-200">
              <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                Immediate Action Plan
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <h5 className="font-medium text-gray-700 mb-2">Week 1-2: Quick Fixes</h5>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Add free shipping calculator</li>
                    <li>• Implement guest checkout</li>
                    <li>• Setup cart reminder emails</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium text-gray-700 mb-2">Week 3-4: UX Improvements</h5>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Simplify checkout forms</li>
                    <li>• Add progress indicators</li>
                    <li>• Enhance size guide</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium text-gray-700 mb-2">Month 2+: Advanced Features</h5>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• AI-powered recommendations</li>
                    <li>• Advanced retargeting</li>
                    <li>• Live chat integration</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cohort Analysis Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200 mt-8">
          {/* Header kısmı */}
          <div className="bg-gradient-to-r from-emerald-700 to-teal-800 text-white p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h3 className="text-base sm:text-lg font-semibold">Customer Cohort Analysis</h3>
                <p className="text-xs sm:text-sm text-emerald-100 mt-1">First-party data retention heatmap by customer acquisition month - Visual retention tracking</p>
              </div>
              <span className="inline-flex items-center px-3 py-1 bg-white/20 text-white text-xs sm:text-sm rounded-full gap-1 whitespace-nowrap">
                <ArrowUpRight className="w-3.5 h-3.5" />
                <span>12-Month Avg Retention: 11.2%</span>
              </span>
            </div>
          </div>
          
          {/* Cohort Heatmap */}
          <div className="p-6">
            {/* Legend */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Retention Rate Heatmap</h4>
                <p className="text-sm text-gray-600">Color intensity represents customer retention percentage</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Low</span>
                  <div className="flex">
                    <div className="w-4 h-4 bg-red-100"></div>
                    <div className="w-4 h-4 bg-yellow-200"></div>
                    <div className="w-4 h-4 bg-green-300"></div>
                    <div className="w-4 h-4 bg-emerald-500"></div>
                    <div className="w-4 h-4 bg-emerald-700"></div>
                  </div>
                  <span className="text-xs text-gray-500">High</span>
                </div>
              </div>
            </div>

            {/* Cohort Grid */}
            <div className="overflow-x-auto">
              <div className="min-w-[900px]">
                {/* Header Row */}
                <div className="grid grid-cols-12 gap-1 mb-2">
                  <div className="text-xs font-semibold text-gray-700 p-2">Cohort</div>
                  <div className="text-xs font-semibold text-gray-700 p-2 text-center">M0</div>
                  <div className="text-xs font-semibold text-gray-700 p-2 text-center">M1</div>
                  <div className="text-xs font-semibold text-gray-700 p-2 text-center">M2</div>
                  <div className="text-xs font-semibold text-gray-700 p-2 text-center">M3</div>
                  <div className="text-xs font-semibold text-gray-700 p-2 text-center">M4</div>
                  <div className="text-xs font-semibold text-gray-700 p-2 text-center">M5</div>
                  <div className="text-xs font-semibold text-gray-700 p-2 text-center">M6</div>
                  <div className="text-xs font-semibold text-gray-700 p-2 text-center">M9</div>
                  <div className="text-xs font-semibold text-gray-700 p-2 text-center">M12</div>
                  <div className="text-xs font-semibold text-gray-700 p-2 text-center">Size</div>
                  <div className="text-xs font-semibold text-gray-700 p-2 text-center">LTV</div>
                </div>

                {/* January 2024 Cohort */}
                <div className="grid grid-cols-12 gap-1 mb-1 items-center">
                  <div className="bg-emerald-50 p-3 rounded text-sm font-medium text-gray-800">Jan '24</div>
                  <div className="bg-emerald-700 text-white p-3 rounded text-center text-sm font-bold">100%</div>
                  <div className="bg-emerald-500 text-white p-3 rounded text-center text-sm font-bold">24.3%</div>
                  <div className="bg-emerald-400 text-white p-3 rounded text-center text-sm font-bold">18.7%</div>
                  <div className="bg-green-400 text-white p-3 rounded text-center text-sm font-bold">15.2%</div>
                  <div className="bg-green-300 text-gray-800 p-3 rounded text-center text-sm font-bold">12.8%</div>
                  <div className="bg-yellow-300 text-gray-800 p-3 rounded text-center text-sm font-bold">11.4%</div>
                  <div className="bg-yellow-200 text-gray-800 p-3 rounded text-center text-sm font-bold">9.8%</div>
                  <div className="bg-red-200 text-gray-800 p-3 rounded text-center text-sm font-bold">7.2%</div>
                  <div className="bg-red-100 text-gray-800 p-3 rounded text-center text-sm font-bold">5.4%</div>
                  <div className="bg-gray-100 text-gray-800 p-3 rounded text-center text-sm">2,847</div>
                  <div className="bg-emerald-100 text-emerald-800 p-3 rounded text-center text-sm font-bold">₺3,247</div>
                </div>

                {/* February 2024 Cohort */}
                <div className="grid grid-cols-12 gap-1 mb-1 items-center">
                  <div className="bg-blue-50 p-3 rounded text-sm font-medium text-gray-800">Feb '24</div>
                  <div className="bg-emerald-700 text-white p-3 rounded text-center text-sm font-bold">100%</div>
                  <div className="bg-emerald-600 text-white p-3 rounded text-center text-sm font-bold">27.8%</div>
                  <div className="bg-emerald-400 text-white p-3 rounded text-center text-sm font-bold">21.3%</div>
                  <div className="bg-green-400 text-white p-3 rounded text-center text-sm font-bold">17.9%</div>
                  <div className="bg-green-300 text-gray-800 p-3 rounded text-center text-sm font-bold">14.2%</div>
                  <div className="bg-yellow-300 text-gray-800 p-3 rounded text-center text-sm font-bold">12.8%</div>
                  <div className="bg-yellow-200 text-gray-800 p-3 rounded text-center text-sm font-bold">10.6%</div>
                  <div className="bg-red-200 text-gray-800 p-3 rounded text-center text-sm font-bold">8.1%</div>
                  <div className="bg-red-100 text-gray-800 p-3 rounded text-center text-sm font-bold">6.3%</div>
                  <div className="bg-gray-100 text-gray-800 p-3 rounded text-center text-sm">3,156</div>
                  <div className="bg-emerald-100 text-emerald-800 p-3 rounded text-center text-sm font-bold">₺2,986</div>
                </div>

                {/* March 2024 Cohort */}
                <div className="grid grid-cols-12 gap-1 mb-1 items-center">
                  <div className="bg-green-50 p-3 rounded text-sm font-medium text-gray-800">Mar '24</div>
                  <div className="bg-emerald-700 text-white p-3 rounded text-center text-sm font-bold">100%</div>
                  <div className="bg-emerald-500 text-white p-3 rounded text-center text-sm font-bold">22.1%</div>
                  <div className="bg-green-400 text-white p-3 rounded text-center text-sm font-bold">16.8%</div>
                  <div className="bg-green-300 text-gray-800 p-3 rounded text-center text-sm font-bold">13.5%</div>
                  <div className="bg-yellow-300 text-gray-800 p-3 rounded text-center text-sm font-bold">11.2%</div>
                  <div className="bg-yellow-200 text-gray-800 p-3 rounded text-center text-sm font-bold">9.8%</div>
                  <div className="bg-red-200 text-gray-800 p-3 rounded text-center text-sm font-bold">8.4%</div>
                  <div className="bg-red-100 text-gray-800 p-3 rounded text-center text-sm font-bold">6.7%</div>
                  <div className="bg-gray-200 text-gray-800 p-3 rounded text-center text-sm">-</div>
                  <div className="bg-gray-100 text-gray-800 p-3 rounded text-center text-sm">2,734</div>
                  <div className="bg-emerald-100 text-emerald-800 p-3 rounded text-center text-sm font-bold">₺2,743</div>
                </div>

                {/* April 2024 Cohort */}
                <div className="grid grid-cols-12 gap-1 mb-1 items-center">
                  <div className="bg-yellow-50 p-3 rounded text-sm font-medium text-gray-800">Apr '24</div>
                  <div className="bg-emerald-700 text-white p-3 rounded text-center text-sm font-bold">100%</div>
                  <div className="bg-emerald-500 text-white p-3 rounded text-center text-sm font-bold">26.4%</div>
                  <div className="bg-green-400 text-white p-3 rounded text-center text-sm font-bold">19.7%</div>
                  <div className="bg-green-300 text-gray-800 p-3 rounded text-center text-sm font-bold">16.3%</div>
                  <div className="bg-yellow-300 text-gray-800 p-3 rounded text-center text-sm font-bold">13.8%</div>
                  <div className="bg-yellow-300 text-gray-800 p-3 rounded text-center text-sm font-bold">11.9%</div>
                  <div className="bg-yellow-200 text-gray-800 p-3 rounded text-center text-sm font-bold">10.2%</div>
                  <div className="bg-red-200 text-gray-800 p-3 rounded text-center text-sm font-bold">7.8%</div>
                  <div className="bg-gray-200 text-gray-800 p-3 rounded text-center text-sm">-</div>
                  <div className="bg-gray-100 text-gray-800 p-3 rounded text-center text-sm">3,421</div>
                  <div className="bg-emerald-100 text-emerald-800 p-3 rounded text-center text-sm font-bold">₺2,456</div>
                </div>

                {/* May 2024 Cohort - Best Performing */}
                <div className="grid grid-cols-12 gap-1 mb-1 items-center border-2 border-emerald-300 rounded-lg p-1">
                  <div className="bg-purple-50 p-3 rounded text-sm font-medium text-gray-800">May '24 ⭐</div>
                  <div className="bg-emerald-700 text-white p-3 rounded text-center text-sm font-bold">100%</div>
                  <div className="bg-emerald-600 text-white p-3 rounded text-center text-sm font-bold">29.3%</div>
                  <div className="bg-emerald-500 text-white p-3 rounded text-center text-sm font-bold">23.7%</div>
                  <div className="bg-emerald-400 text-white p-3 rounded text-center text-sm font-bold">19.4%</div>
                  <div className="bg-green-400 text-white p-3 rounded text-center text-sm font-bold">16.8%</div>
                  <div className="bg-green-300 text-gray-800 p-3 rounded text-center text-sm font-bold">14.2%</div>
                  <div className="bg-yellow-300 text-gray-800 p-3 rounded text-center text-sm font-bold">12.4%</div>
                  <div className="bg-gray-200 text-gray-800 p-3 rounded text-center text-sm">-</div>
                  <div className="bg-gray-200 text-gray-800 p-3 rounded text-center text-sm">-</div>
                  <div className="bg-gray-100 text-gray-800 p-3 rounded text-center text-sm">2,956</div>
                  <div className="bg-emerald-200 text-emerald-800 p-3 rounded text-center text-sm font-bold">₺2,834</div>
                </div>

                {/* June 2024 Cohort */}
                <div className="grid grid-cols-12 gap-1 mb-1 items-center">
                  <div className="bg-indigo-50 p-3 rounded text-sm font-medium text-gray-800">Jun '24</div>
                  <div className="bg-emerald-700 text-white p-3 rounded text-center text-sm font-bold">100%</div>
                  <div className="bg-emerald-400 text-white p-3 rounded text-center text-sm font-bold">21.8%</div>
                  <div className="bg-green-400 text-white p-3 rounded text-center text-sm font-bold">17.3%</div>
                  <div className="bg-green-300 text-gray-800 p-3 rounded text-center text-sm font-bold">14.6%</div>
                  <div className="bg-yellow-300 text-gray-800 p-3 rounded text-center text-sm font-bold">12.1%</div>
                  <div className="bg-yellow-200 text-gray-800 p-3 rounded text-center text-sm font-bold">10.4%</div>
                  <div className="bg-red-200 text-gray-800 p-3 rounded text-center text-sm font-bold">8.9%</div>
                  <div className="bg-gray-200 text-gray-800 p-3 rounded text-center text-sm">-</div>
                  <div className="bg-gray-200 text-gray-800 p-3 rounded text-center text-sm">-</div>
                  <div className="bg-gray-100 text-gray-800 p-3 rounded text-center text-sm">3,847</div>
                  <div className="bg-emerald-100 text-emerald-800 p-3 rounded text-center text-sm font-bold">₺2,145</div>
                </div>

                {/* July 2024 Cohort */}
                <div className="grid grid-cols-12 gap-1 mb-1 items-center">
                  <div className="bg-orange-50 p-3 rounded text-sm font-medium text-gray-800">Jul '24</div>
                  <div className="bg-emerald-700 text-white p-3 rounded text-center text-sm font-bold">100%</div>
                  <div className="bg-green-400 text-white p-3 rounded text-center text-sm font-bold">19.5%</div>
                  <div className="bg-green-300 text-gray-800 p-3 rounded text-center text-sm font-bold">15.2%</div>
                  <div className="bg-yellow-300 text-gray-800 p-3 rounded text-center text-sm font-bold">12.3%</div>
                  <div className="bg-yellow-200 text-gray-800 p-3 rounded text-center text-sm font-bold">10.1%</div>
                  <div className="bg-red-200 text-gray-800 p-3 rounded text-center text-sm font-bold">8.7%</div>
                  <div className="bg-gray-200 text-gray-800 p-3 rounded text-center text-sm">-</div>
                  <div className="bg-gray-200 text-gray-800 p-3 rounded text-center text-sm">-</div>
                  <div className="bg-gray-200 text-gray-800 p-3 rounded text-center text-sm">-</div>
                  <div className="bg-gray-100 text-gray-800 p-3 rounded text-center text-sm">4,123</div>
                  <div className="bg-yellow-100 text-yellow-800 p-3 rounded text-center text-sm font-bold">₺1,847</div>
                </div>

                {/* August 2024 Cohort */}
                <div className="grid grid-cols-12 gap-1 mb-1 items-center">
                  <div className="bg-red-50 p-3 rounded text-sm font-medium text-gray-800">Aug '24</div>
                  <div className="bg-emerald-700 text-white p-3 rounded text-center text-sm font-bold">100%</div>
                  <div className="bg-green-300 text-gray-800 p-3 rounded text-center text-sm font-bold">17.2%</div>
                  <div className="bg-yellow-300 text-gray-800 p-3 rounded text-center text-sm font-bold">13.8%</div>
                  <div className="bg-yellow-200 text-gray-800 p-3 rounded text-center text-sm font-bold">11.4%</div>
                  <div className="bg-red-200 text-gray-800 p-3 rounded text-center text-sm font-bold">9.2%</div>
                  <div className="bg-gray-200 text-gray-800 p-3 rounded text-center text-sm">-</div>
                  <div className="bg-gray-200 text-gray-800 p-3 rounded text-center text-sm">-</div>
                  <div className="bg-gray-200 text-gray-800 p-3 rounded text-center text-sm">-</div>
                  <div className="bg-gray-200 text-gray-800 p-3 rounded text-center text-sm">-</div>
                  <div className="bg-gray-100 text-gray-800 p-3 rounded text-center text-sm">3,892</div>
                  <div className="bg-yellow-100 text-yellow-800 p-3 rounded text-center text-sm font-bold">₺1,623</div>
                </div>
              </div>
            </div>

            {/* Key Insights Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-6 border border-emerald-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-lg">🏆</span>
                  </div>
                  <h4 className="font-semibold text-emerald-800">Best Performers</h4>
                </div>
                <ul className="text-sm text-emerald-700 space-y-2">
                  <li>• <strong>May 2024:</strong> En yüksek LTV (₺2,834)</li>
                  <li>• <strong>Feb 2024:</strong> En iyi 1. ay retention (27.8%)</li>
                  <li>• <strong>Jan 2024:</strong> En uzun süre retention</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-6 border border-red-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-lg">⚠️</span>
                  </div>
                  <h4 className="font-semibold text-red-800">Concern Areas</h4>
                </div>
                <ul className="text-sm text-red-700 space-y-2">
                  <li>• <strong>Yaz cohortları:</strong> Düşük retention oranları</li>
                  <li>• <strong>3-6. aylar:</strong> En dik retention düşüşü</li>
                  <li>• <strong>Ağustos 2024:</strong> En düşük performans</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-lg">💡</span>
                  </div>
                  <h4 className="font-semibold text-blue-800">Key Insights</h4>
                </div>
                <ul className="text-sm text-blue-700 space-y-2">
                  <li>• <strong>Hediye sezonları:</strong> Daha iyi performans</li>
                  <li>• <strong>AOV artışı:</strong> Sadık müşteriler %40 daha fazla</li>
                  <li>• <strong>Kritik dönem:</strong> 2-4. aylar dikkat gerektirir</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Cohort Analysis Insights & First-Party Data Applications */}
          <div className="p-6 bg-gray-50 border-t">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column: First-Party Data Applications */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                  E-ticaret First-Party Data Uygulamaları
                </h4>
                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-blue-200">
                    <h5 className="font-medium text-blue-700 mb-2">🎯 Customer Lifetime Value Optimization</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• <strong>Budget allocation:</strong> Yüksek LTV cohortlara reklam bütçesi odaklama</li>
                      <li>• <strong>Pricing strategy:</strong> Sadık segmentler için premium fiyatlandırma</li>
                      <li>• <strong>Product development:</strong> Değerli cohortlar için ürün geliştirme</li>
                    </ul>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-green-200">
                    <h5 className="font-medium text-green-700 mb-2">📧 Personalized Marketing Campaigns</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• <strong>Email automation:</strong> Kritik retention noktalarında otomatik kampanyalar</li>
                      <li>• <strong>Win-back campaigns:</strong> 3-4. ay düşüş segmentlerine hedefli kampanyalar</li>
                      <li>• <strong>Lookalike audiences:</strong> En iyi cohortlara benzer profilleri bulma</li>
                    </ul>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-purple-200">
                    <h5 className="font-medium text-purple-700 mb-2">🛍️ Inventory & Product Strategy</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• <strong>Seasonal planning:</strong> Cohort satın alma kalıplarına göre stok planlama</li>
                      <li>• <strong>Cross-sell optimization:</strong> Sadık segmentler için ürün paketleme</li>
                      <li>• <strong>New product launches:</strong> Kanıtlanmış satın alma gücü olan cohortları hedefleme</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Right Column: Strategic Actions */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <div className="w-4 h-4 bg-emerald-500 rounded-full"></div>
                  Cohort Verilerine Dayalı Stratejik Aksiyonlar
                </h4>
                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-orange-200">
                    <h5 className="font-medium text-orange-700 mb-2">🚀 Hafta 1-2: Hızlı Kazanımlar</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• "3. Ay Geri Dönüş" e-posta serisi oluştur</li>
                      <li>• 6+ ay müşterileri için otomatik sadakat programı</li>
                      <li>• Haziran cohort için geri kazanım kampanyası</li>
                    </ul>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-cyan-200">
                    <h5 className="font-medium text-cyan-700 mb-2">📊 Ay 1: Veri İmplementasyonu</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Cohort verisi kullanarak predictive churn modelleri</li>
                      <li>• Personalizasyon için dinamik müşteri segmentleri</li>
                      <li>• Real-time LTV skorlama sistemi</li>
                    </ul>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-indigo-200">
                    <h5 className="font-medium text-indigo-700 mb-2">🎯 Çeyrek 1: Stratejik Girişimler</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• En yüksek %10 LTV müşteriler için VIP program</li>
                      <li>• Seasonal acquisition stratejileri geliştir</li>
                      <li>• Cohort-based envanter planlama sistemi</li>
                    </ul>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-emerald-200">
                    <h5 className="font-medium text-emerald-700 mb-2">💰 Revenue Forecasting & Planning</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• <strong>Revenue prediction:</strong> Cohort davranışına dayalı gelir tahmini</li>
                      <li>• <strong>Churn prevention:</strong> Risk altındaki müşterileri erken tespit</li>
                      <li>• <strong>Investment decisions:</strong> Retention programları için ROI hesaplama</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Funnel Behavior Analysis */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200 mt-8">
          {/* Header kısmı */}
          <div className="bg-gradient-to-r from-indigo-700 to-purple-800 text-white p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h3 className="text-base sm:text-lg font-semibold">Category Competition Funnel</h3>
                <p className="text-xs sm:text-sm text-indigo-100 mt-1">Homepage → Category Listings → Product Views - Category performance battle</p>
              </div>
              <span className="inline-flex items-center px-3 py-1 bg-white/20 text-white text-xs sm:text-sm rounded-full gap-1 whitespace-nowrap">
                <ArrowUpRight className="w-3.5 h-3.5" />
                <span>Total Flow: 156K → 98K → 45K</span>
              </span>
            </div>
          </div>
          
          {/* Simplified Funnel Visualization */}
          <div className="p-8">
            <div className="max-w-5xl mx-auto">
              {/* Step 1: Homepage */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-6 rounded-2xl shadow-xl">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-2xl">🏠</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">Homepage</h3>
                    <p className="text-lg opacity-90">156,420 views</p>
                  </div>
                </div>
                <div className="mt-4">
                  <ArrowDown className="w-8 h-8 text-blue-600 mx-auto" />
                </div>
              </div>

              {/* Step 2: Category Listings Competition */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-center mb-6 text-gray-800">Category Listings Battle</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Takım Elbise - Winner */}
                  <div className="relative">
                    <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl shadow-lg p-6 border-4 border-yellow-400">
                      <div className="absolute -top-3 -right-3 bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-sm font-bold">
                        👑 #1
                      </div>
                      <div className="text-center">
                        <div className="text-4xl mb-2">👔</div>
                        <h4 className="text-xl font-bold">Takım Elbise</h4>
                        <div className="text-2xl font-bold mt-2">33,540</div>
                        <div className="text-sm opacity-80">views (34.0%)</div>
                        <div className="mt-3 w-full bg-white/20 rounded-full h-2">
                          <div className="bg-white h-2 rounded-full" style={{ width: '100%' }}></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Gömlek - Second */}
                  <div className="relative">
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl shadow-lg p-6 border-2 border-gray-300">
                      <div className="absolute -top-3 -right-3 bg-gray-300 text-gray-700 px-3 py-1 rounded-full text-sm font-bold">
                        🥈 #2
                      </div>
                      <div className="text-center">
                        <div className="text-4xl mb-2">👕</div>
                        <h4 className="text-xl font-bold">Gömlek</h4>
                        <div className="text-2xl font-bold mt-2">27,653</div>
                        <div className="text-sm opacity-80">views (28.0%)</div>
                        <div className="mt-3 w-full bg-white/20 rounded-full h-2">
                          <div className="bg-white h-2 rounded-full" style={{ width: '82%' }}></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Aksesuar - Third */}
                  <div className="relative">
                    <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl shadow-lg p-6 border-2 border-orange-300">
                      <div className="absolute -top-3 -right-3 bg-orange-300 text-orange-700 px-3 py-1 rounded-full text-sm font-bold">
                        🥉 #3
                      </div>
                      <div className="text-center">
                        <div className="text-4xl mb-2">👞</div>
                        <h4 className="text-xl font-bold">Aksesuar</h4>
                        <div className="text-2xl font-bold mt-2">20,740</div>
                        <div className="text-sm opacity-80">views (21.0%)</div>
                        <div className="mt-3 w-full bg-white/20 rounded-full h-2">
                          <div className="bg-white h-2 rounded-full" style={{ width: '62%' }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Other Categories */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                  <div className="bg-gray-100 rounded-lg p-4 text-center">
                    <div className="text-2xl mb-1">🧥</div>
                    <div className="font-semibold">Ceket</div>
                    <div className="text-lg font-bold text-gray-700">9,876</div>
                    <div className="text-sm text-gray-500">10.0%</div>
                  </div>
                  <div className="bg-gray-100 rounded-lg p-4 text-center">
                    <div className="text-2xl mb-1">👖</div>
                    <div className="font-semibold">Pantolon</div>
                    <div className="text-lg font-bold text-gray-700">4,938</div>
                    <div className="text-sm text-gray-500">5.0%</div>
                  </div>
                  <div className="bg-gray-100 rounded-lg p-4 text-center">
                    <div className="text-2xl mb-1">👔</div>
                    <div className="font-semibold">Kravat</div>
                    <div className="text-lg font-bold text-gray-700">1,975</div>
                    <div className="text-sm text-gray-500">2.0%</div>
                  </div>
                  <div className="bg-gray-100 rounded-lg p-4 text-center">
                    <div className="text-2xl mb-1">🕶️</div>
                    <div className="font-semibold">Diğer</div>
                    <div className="text-lg font-bold text-gray-700">0</div>
                    <div className="text-sm text-gray-500">0%</div>
                  </div>
                </div>

                <div className="text-center mt-6">
                  <div className="inline-flex items-center bg-gray-200 text-gray-800 px-6 py-3 rounded-full">
                    <strong>Total Category Views: 98,722</strong>
                  </div>
                </div>
                
                <div className="text-center mt-4">
                  <ArrowDown className="w-8 h-8 text-gray-600 mx-auto" />
                </div>
              </div>

              {/* Step 3: Product Views Competition */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-center mb-6 text-gray-800">Product Detail Views</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Takım Elbise Products */}
                  <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-6 border-2 border-emerald-300">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-2 mb-3">
                        <div className="text-2xl">👔</div>
                        <h4 className="text-lg font-bold text-emerald-800">Takım Elbise</h4>
                        <span className="bg-emerald-200 text-emerald-800 px-2 py-1 rounded-full text-xs">Lider</span>
                      </div>
                      <div className="text-3xl font-bold text-emerald-700">15,381</div>
                      <div className="text-sm text-emerald-600 mb-4">product views (34.0%)</div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between bg-white/50 rounded px-2 py-1">
                          <span>Klasik Takımlar</span>
                          <span className="font-semibold">8,450</span>
                        </div>
                        <div className="flex justify-between bg-white/50 rounded px-2 py-1">
                          <span>Slim Fit</span>
                          <span className="font-semibold">4,231</span>
                        </div>
                        <div className="flex justify-between bg-white/50 rounded px-2 py-1">
                          <span>Ceremonial</span>
                          <span className="font-semibold">2,700</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Gömlek Products */}
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border-2 border-blue-300">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-2 mb-3">
                        <div className="text-2xl">👕</div>
                        <h4 className="text-lg font-bold text-blue-800">Gömlek</h4>
                        <span className="bg-blue-200 text-blue-800 px-2 py-1 rounded-full text-xs">2.</span>
                      </div>
                      <div className="text-3xl font-bold text-blue-700">12,607</div>
                      <div className="text-sm text-blue-600 mb-4">product views (28.0%)</div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between bg-white/50 rounded px-2 py-1">
                          <span>Klasik Gömlek</span>
                          <span className="font-semibold">5,673</span>
                        </div>
                        <div className="flex justify-between bg-white/50 rounded px-2 py-1">
                          <span>Casual</span>
                          <span className="font-semibold">3,782</span>
                        </div>
                        <div className="flex justify-between bg-white/50 rounded px-2 py-1">
                          <span>Premium</span>
                          <span className="font-semibold">3,152</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Aksesuar Products */}
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 border-2 border-orange-300">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-2 mb-3">
                        <div className="text-2xl">👞</div>
                        <h4 className="text-lg font-bold text-orange-800">Aksesuar</h4>
                        <span className="bg-orange-200 text-orange-800 px-2 py-1 rounded-full text-xs">3.</span>
                      </div>
                      <div className="text-3xl font-bold text-orange-700">9,468</div>
                      <div className="text-sm text-orange-600 mb-4">product views (21.0%)</div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between bg-white/50 rounded px-2 py-1">
                          <span>Ayakkabı</span>
                          <span className="font-semibold">4,734</span>
                        </div>
                        <div className="flex justify-between bg-white/50 rounded px-2 py-1">
                          <span>Çanta</span>
                          <span className="font-semibold">2,840</span>
                        </div>
                        <div className="flex justify-between bg-white/50 rounded px-2 py-1">
                          <span>Saat</span>
                          <span className="font-semibold">1,894</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Other Category Product Views */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                  <div className="bg-gray-50 rounded-lg p-4 text-center border">
                    <div className="text-xl mb-1">🧥</div>
                    <div className="font-semibold text-gray-700">Ceket</div>
                    <div className="text-xl font-bold text-gray-800">4,509</div>
                    <div className="text-sm text-gray-500">10.0%</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center border">
                    <div className="text-xl mb-1">👖</div>
                    <div className="font-semibold text-gray-700">Pantolon</div>
                    <div className="text-xl font-bold text-gray-800">2,255</div>
                    <div className="text-sm text-gray-500">5.0%</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center border">
                    <div className="text-xl mb-1">👔</div>
                    <div className="font-semibold text-gray-700">Kravat</div>
                    <div className="text-xl font-bold text-gray-800">901</div>
                    <div className="text-sm text-gray-500">2.0%</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center border">
                    <div className="text-xl mb-1">🔍</div>
                    <div className="font-semibold text-gray-700">Diğer</div>
                    <div className="text-xl font-bold text-gray-800">99</div>
                    <div className="text-sm text-gray-500">0.2%</div>
                  </div>
                </div>

                <div className="text-center mt-6">
                  <div className="inline-flex items-center bg-indigo-100 text-indigo-800 px-6 py-3 rounded-full">
                    <strong>Total Product Views: 45,220</strong>
                  </div>
                </div>
              </div>

              {/* Performance Summary */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-6">
                <h3 className="text-xl font-bold text-center mb-4 text-gray-800">Category Performance Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-3xl mb-2">🏆</div>
                    <h4 className="font-bold text-emerald-700">Champion: Takım Elbise</h4>
                    <p className="text-sm text-gray-600">En yüksek listing-to-product conversion</p>
                    <div className="text-xl font-bold text-emerald-600 mt-2">45.8%</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl mb-2">📈</div>
                    <h4 className="font-bold text-blue-700">Rising Star: Gömlek</h4>
                    <p className="text-sm text-gray-600">En istikrarlı performance</p>
                    <div className="text-xl font-bold text-blue-600 mt-2">45.6%</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl mb-2">⚡</div>
                    <h4 className="font-bold text-orange-700">Opportunity: Aksesuar</h4>
                    <p className="text-sm text-gray-600">Improvement potential</p>
                    <div className="text-xl font-bold text-orange-600 mt-2">45.6%</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Category Competition Insights */}
          <div className="p-6 bg-gray-50 border-t">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column: Winning Categories */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <div className="w-4 h-4 bg-emerald-500 rounded-full"></div>
                  Category Winners & Strategies
                </h4>
                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-emerald-200">
                    <h5 className="font-medium text-emerald-700 mb-2">🏆 Takım Elbise Strategy</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• <strong>Homepage placement:</strong> Hero section prominence</li>
                      <li>• <strong>Search optimization:</strong> High keyword visibility</li>
                      <li>• <strong>Product variety:</strong> Klasik, Slim Fit, Ceremonial options</li>
                    </ul>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 border border-blue-200">
                    <h5 className="font-medium text-blue-700 mb-2">🥈 Gömlek Stability</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• <strong>Consistent performance:</strong> Stable listing views</li>
                      <li>• <strong>Product mix:</strong> Balanced klassik-casual-premium</li>
                      <li>• <strong>Cross-sell potential:</strong> Pairs well with other categories</li>
                    </ul>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-orange-200">
                    <h5 className="font-medium text-orange-700 mb-2">🥉 Aksesuar Opportunity</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• <strong>Diverse portfolio:</strong> Ayakkabı, çanta, saat variety</li>
                      <li>• <strong>Upsell potential:</strong> Complement main categories</li>
                      <li>• <strong>Improvement area:</strong> Better homepage visibility needed</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Right Column: Action Plan */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <div className="w-4 h-4 bg-indigo-500 rounded-full"></div>
                  Category Competition Action Plan
                </h4>
                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-purple-200">
                    <h5 className="font-medium text-purple-700 mb-2">🚀 Quick Wins (Week 1-2)</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Boost Aksesuar homepage visibility</li>
                      <li>• Add "Complete the Look" suggestions</li>
                      <li>• Improve Pantolon category navigation</li>
                    </ul>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-cyan-200">
                    <h5 className="font-medium text-cyan-700 mb-2">📊 Medium-term (Month 1)</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• A/B test category page layouts</li>
                      <li>• Implement smart category recommendations</li>
                      <li>• Optimize search for underperforming categories</li>
                    </ul>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-green-200">
                    <h5 className="font-medium text-green-700 mb-2">🎯 Strategic (Quarter 1)</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Develop category-specific landing pages</li>
                      <li>• Create seasonal category promotion calendar</li>
                      <li>• Build competitive category analytics dashboard</li>
                    </ul>
                  </div>

                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-200">
                    <h5 className="font-medium text-indigo-700 mb-2">📈 Success Metrics</h5>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div>• Category listing views growth</div>
                      <div>• Product view conversion rates</div>
                      <div>• Cross-category navigation patterns</div>
                      <div>• Homepage → Category → Product flow</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 